import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

// material-ui
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTheme, alpha } from '@mui/material/styles';

import BodyDiagram from 'components/BodyDiagram';
import ServerImage from 'components/ServerImage';
import DailyMandDetail from 'components/calendar/DailyMandDetail';
import BehaviorIntervalRecordingsView from 'components/calendar/BehaviorIntervalRecordingsView';

// ant design icons
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import EnvironmentOutlined from '@ant-design/icons/EnvironmentOutlined';
import CaretDownOutlined from '@ant-design/icons/CaretDownOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import PlayCircleOutlined from '@ant-design/icons/PlayCircleOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import StopOutlined from '@ant-design/icons/StopOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import LeftOutlined from '@ant-design/icons/LeftOutlined';
import RightOutlined from '@ant-design/icons/RightOutlined';
import LayoutOutlined from '@ant-design/icons/LayoutOutlined';
import AppstoreOutlined from '@ant-design/icons/AppstoreOutlined';
import OrderedListOutlined from '@ant-design/icons/OrderedListOutlined';
import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined';
import MinusCircleOutlined from '@ant-design/icons/MinusCircleOutlined';
import MoreOutlined from '@ant-design/icons/MoreOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';

// material-ui extras
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

// material-ui extras - additional
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';

// project imports
import MainCard from 'components/MainCard';
import SpeechTextField from 'components/SpeechTextField';
import CommunicationDiaryTab from 'sections/shared/child-detail/CommunicationDiaryTab';
import { PopupTransition } from 'components/@extended/Transitions';
import EditSessionDialog from 'components/calendar/EditSessionDialog';
import { listClients } from 'services/api/clients';
import {
  listSessions,
  startSession as apiStartSession,
  completeSession,
  getSessionData,
  updateSession,
  cancelSession,
  markNoShow,
  createSession,
  deleteSession as apiDeleteSession,
  getSessionReferenceData
} from 'services/api/sessions';
import { transformSession } from 'utils/apiTransformers';
import { formatActivityList } from 'utils/activityList';
import { toPromptCodeArray } from 'utils/promptLevels';
import { childDisplayName } from 'utils/childDisplayName';
import { getDateLocaleTag } from 'utils/dateLocale';
import { newSeriesId, describeRecurrence, clampRecurrenceRule } from 'utils/sessionSeries';
import { canRunSession, runnerBlockedReason } from 'utils/sessionRunner';
import { MIN_SESSION_MINUTES, MAX_SESSION_MINUTES, isValidSessionDuration, parseDurationInput } from 'utils/sessionDuration';
import { isFutureSessionDate, isBeforeConductWindow, scheduledStartOf } from 'utils/normalizeDate';
import { computeDayColumnLayout, gridBodyHeight } from 'utils/weekCalendarLayout';
import {
  updateCachedSession,
  saveSessionUpdate,
  upsertSessionsForTherapist,
  getCachedSessionsForTherapist,
  getCachedSessions,
  SESSION_UPDATE_TYPES
} from 'utils/offlineDB';
import useOnlineStatus from 'hooks/useOnlineStatus';
import useAuth from 'hooks/useAuth';
import useHiddenCatalogue from 'hooks/useHiddenCatalogue';
import usePageAccess from 'hooks/usePageAccess';
import usePageIcon from 'hooks/usePageIcon';

// Features and Pages code for the "Session Calendar" page (Calendar feature). Switching
// it off — globally OR for the role in Role Definitions — leaves the My Calendar page
// itself intact (that is gated by `my_calendar`) but renders NO session events on it.
// This is a DIFFERENT switch from the child profile's "Scheduled Sessions" tab
// (`child_scheduled_sessions`), which gates that tab, not the calendar.
const SESSION_CALENDAR_CODE = 'session_calendar';

// Features and Pages code for the "Attendance" page (Calendar feature). Switching it off
// — globally OR for the role in Role Definitions — hides the session STATUS chips
// (Present / Absent / Upcoming / Missed / Completed) wherever a session is shown; the
// session itself still renders, just without its attendance state.
const ATTENDANCE_CODE = 'attendance';

/* What governs the diary button in the session dialog.
 *
 * `session_communication_diary` is its OWN switch (Features and Components → Data Tracking),
 * so an administrator can close the therapist's in-session route to the diary while leaving
 * the child profile's diary tab and the caregiver's own screen open, or the other way round.
 * They are separate decisions and used to share one switch.
 *
 * `communication_diary` is the FEATURE, and it still closes everything: with the capability
 * switched off there is no diary to offer from anywhere.
 */
const COMM_DIARY_CODES = ['session_communication_diary', 'communication_diary'];

/* How every action in the session dialog's footer sizes itself.
 *
 * NATURAL WIDTH in a row, full width once the row becomes a column on a phone. They used to
 * be `flex: 1 1 auto` each, which on a wrapping row makes whichever button wraps last grow
 * to fill the line on its own — Delete ended up stretched across the whole dialog under four
 * normal-sized buttons. Growth is what has to go, not the wrapping.
 */
const ACTION_SX = { flex: { xs: '1 1 auto', sm: '0 0 auto' } };

// Helper: format a Date to 'YYYY-MM-DD' in LOCAL timezone (avoids UTC shift from toISOString)
const toLocalDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Helper: format datetime as local ISO string without timezone
const toLocalISOStr = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

// ICS helpers
const toICSDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}T${pad(dt.getHours())}${pad(dt.getMinutes())}${pad(dt.getSeconds())}`;
};

const buildVEVENT = (event) => {
  const startDT = event.date && event.startTime ? new Date(`${event.date}T${event.startTime}:00`) : null;
  const endDT = event.date && event.endTime ? new Date(`${event.date}T${event.endTime}:00`) : null;
  const start = toICSDate(startDT || event.start);
  const end = toICSDate(endDT || event.end);
  const summary = event.childName ? `Session - ${event.childName}` : 'Therapy Session';
  const description = [
    event.program ? `Program: ${event.program}` : '',
    event.childName ? `Child: ${event.childName}` : '',
    event.sessionType ? `Type: ${event.sessionType}` : '',
    event.status ? `Status: ${event.status}` : '',
    event.notes || ''
  ]
    .filter(Boolean)
    .join('\\n');
  return [
    'BEGIN:VEVENT',
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    `UID:${event.id || Date.now()}@ayro-platform`,
    `DTSTAMP:${toICSDate(new Date())}`,
    'END:VEVENT'
  ].join('\r\n');
};

const exportCalendarAsICS = (events, filename) => {
  if (!events || events.length === 0) return;
  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Ayro Platform//Therapist Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    ...events.map(buildVEVENT),
    'END:VCALENDAR'
  ].join('\r\n');
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const parseICSDateTime = (value) => {
  if (!value) return null;
  const clean = value.replace('Z', '');
  if (clean.length < 15) return null;
  return new Date(
    parseInt(clean.substring(0, 4)),
    parseInt(clean.substring(4, 6)) - 1,
    parseInt(clean.substring(6, 8)),
    parseInt(clean.substring(9, 11)),
    parseInt(clean.substring(11, 13)),
    parseInt(clean.substring(13, 15))
  );
};

const parseAllICSEvents = (icsContent) => {
  const unfolded = icsContent.replace(/\r\n[ \t]/g, '').replace(/\n[ \t]/g, '');
  const lines = unfolded.split(/\r?\n/);
  const events = [];
  let inEvent = false;
  let current = null;

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed === 'BEGIN:VEVENT') {
      inEvent = true;
      current = { summary: '', description: '', dtstart: null, dtend: null, duration: 60 };
      continue;
    }
    if (trimmed === 'END:VEVENT') {
      inEvent = false;
      if (current) {
        if (current.dtstart && current.dtend) {
          current.duration = Math.round((current.dtend - current.dtstart) / 60000);
        }
        const descLines = (current.description || '').split('\n');
        for (const dl of descLines) {
          const t = dl.trim();
          if (t.startsWith('Child:')) current.childHint = t.replace('Child:', '').trim();
          if (t.startsWith('Program:')) current.programHint = t.replace('Program:', '').trim();
          if (t.startsWith('Type:')) current.typeHint = t.replace('Type:', '').trim();
        }
        events.push(current);
        current = null;
      }
      continue;
    }
    if (!inEvent || !current) continue;
    const colonIdx = line.indexOf(':');
    if (colonIdx === -1) continue;
    const key = line.substring(0, colonIdx).split(';')[0].toUpperCase();
    const value = line.substring(colonIdx + 1);
    switch (key) {
      case 'SUMMARY':
        current.summary = value;
        break;
      case 'DESCRIPTION':
        current.description = value.replace(/\\n/g, '\n');
        break;
      case 'DTSTART':
        current.dtstart = parseICSDateTime(value);
        break;
      case 'DTEND':
        current.dtend = parseICSDateTime(value);
        break;
      default:
        break;
    }
  }
  return events;
};

// View options for the toolbar
const viewOptions = [
  { labelId: 'calendar.view.week', value: 'week', icon: LayoutOutlined },
  { labelId: 'calendar.view.month', value: 'month', icon: AppstoreOutlined },
  { labelId: 'calendar.view.agenda', value: 'agenda', icon: OrderedListOutlined }
];

// Time slots for week view (7 AM to 6 PM)
const timeSlots = [];
for (let hour = 7; hour <= 18; hour++) {
  timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
}
// Geometry for the proportional-height week grid — see the matching block in the
// supervisor calendar. HOUR_HEIGHT is one hour row's pixel height; the window
// hours are derived from timeSlots so event placement can never drift from the
// rows rendered.
const HOUR_HEIGHT = 60;
const WEEK_START_HOUR = parseInt(timeSlots[0], 10); // 7
const WEEK_END_HOUR = parseInt(timeSlots[timeSlots.length - 1], 10) + 1; // 19

// ==============================|| THERAPIST CALENDAR ||============================== //

const TherapistCalendar = ({ isOfflineMode = false, offlineSessions = [], offlineChildren = [] }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  const dateLocaleTag = getDateLocaleTag(intl.locale);
  const { user } = useAuth();
  const { isOnline } = useOnlineStatus();
  const downSM = useMediaQuery(theme.breakpoints.down('sm'));
  // Match the sidebar: use the icon configured in Features and Components for
  // this route, falling back to the static one until the catalogue loads.
  const HeaderIcon = usePageIcon(CalendarOutlined);

  // When the "Session Calendar" page is switched off — globally in Features and Pages OR
  // for this therapist's role in Role Definitions (useHiddenCatalogue combines both) —
  // therapists must not see scheduled sessions. Null means "not yet known" → fail open
  // (show sessions) rather than blank the calendar on a pending request, per the app-wide rule.
  const hiddenCatalogue = useHiddenCatalogue();
  // Full-Read on the Calendar feature (or its pages): view + export only. No importing,
  // starting, ending, cancelling, or deleting sessions. The backend also refuses these.
  const { readOnly: isCalCodeReadOnly } = usePageAccess();
  const calReadOnly = isCalCodeReadOnly('calendar') || isCalCodeReadOnly('my_calendar') || isCalCodeReadOnly('session_calendar');
  const scheduledSessionsHidden = useMemo(() => {
    return hiddenCatalogue ? hiddenCatalogue.codeHidden(SESSION_CALENDAR_CODE) : false;
  }, [hiddenCatalogue]);

  // Attendance page off → don't render the session status chips anywhere on the calendar.
  const attendanceHidden = useMemo(() => {
    return hiddenCatalogue ? hiddenCatalogue.codeHidden(ATTENDANCE_CODE) : false;
  }, [hiddenCatalogue]);

  const diaryHidden = useMemo(() => {
    return hiddenCatalogue ? COMM_DIARY_CODES.some((code) => hiddenCatalogue.codeHidden(code)) : false;
  }, [hiddenCatalogue]);

  /* The diary, read (and written) WITHOUT leaving the session dialog.
   *
   * Opened over the dialog rather than navigated to, because everything the therapist has
   * already done here — the attendance they picked, the pre-session note they are half
   * way through typing — lives in this component's state and would be gone on the way
   * back. See CommunicationDiaryTab's `overlay` mode. */
  const [diaryOpen, setDiaryOpen] = useState(false);

  // View state
  const [viewMode, setViewMode] = useState('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedChild, setSelectedChild] = useState('all');

  // State for managing sessions - use offline sessions if in offline mode
  const [sessions, setSessions] = useState(isOfflineMode ? offlineSessions : []);

  // Load sessions from API on mount, navigation back, or date change
  useEffect(() => {
    if (!isOfflineMode) {
      loadSessions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOfflineMode, location.key, currentDate, isOnline]);

  // Read sessions from IndexedDB (used when offline or when the API is
  // unreachable) so the online calendar route still shows what was last cached.
  const loadSessionsFromCache = async () => {
    try {
      const personId = user?.person_id;
      let cached = personId ? await getCachedSessionsForTherapist(personId) : [];
      if (!cached || cached.length === 0) {
        cached = await getCachedSessions();
      }
      setSessions(cached || []);
    } catch (cacheErr) {
      console.warn('[Calendar] Failed to load cached sessions:', cacheErr.message);
      setSessions([]);
    }
  };

  const loadSessions = async () => {
    // Offline: skip the API entirely and read straight from the cache.
    if (!isOnline) {
      await loadSessionsFromCache();
      return;
    }
    try {
      // Compute a generous date range: current month ± 1 week buffer
      const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      const bufferStart = new Date(monthStart);
      bufferStart.setDate(bufferStart.getDate() - 7);
      const bufferEnd = new Date(monthEnd);
      bufferEnd.setDate(bufferEnd.getDate() + 7);

      const formatDate = toLocalDateStr;

      const params = {
        therapist_id: user?.person_id,
        start_date: formatDate(bufferStart),
        end_date: formatDate(bufferEnd),
        page_size: 200
      };
      const res = await listSessions(params);
      const items = res?.items || res || [];
      const transformed = items.map(transformSession);
      setSessions(transformed);

      // Mirror what we just loaded into IndexedDB (scoped to this therapist) so
      // the offline calendar shows the same sessions the therapist saw online.
      // Non-destructive upsert: browsing accumulates the cache across months.
      if (user?.person_id) {
        upsertSessionsForTherapist(transformed, user.person_id).catch((err) =>
          console.warn('[Calendar] Failed to cache sessions for offline:', err.message)
        );
      }
    } catch (error) {
      console.error('[Calendar] Failed to load sessions:', error);
      // API failed (often a dropped connection that the offline detector hasn't
      // confirmed yet) — fall back to cached sessions instead of a blank view.
      await loadSessionsFromCache();
    }
  };

  // Dialog states
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [sessionCollectedData, setSessionCollectedData] = useState([]);
  const [isLoadingCollectedData, setIsLoadingCollectedData] = useState(false);
  // Inline dialog used by the "View Data" button on the Session Details modal.
  // Showing the collected entries here (instead of navigating to a separate
  // page) keeps the therapist on the calendar and side-steps the routing race
  // we hit before where the navigate was getting swallowed.
  const [viewDataOpen, setViewDataOpen] = useState(false);
  // Drill-down: clicking "View details" on any data-sheet card inside the
  // Session Data dialog opens this second dialog. The shape is
  // { name, formType, rawData } so the renderer can dispatch on formType.
  const [detailEntry, setDetailEntry] = useState(null);
  const [isAddDialogOpen, setAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteMode, setDeleteMode] = useState('single'); // 'single' | 'following' | 'series'
  const [editScope, setEditScope] = useState(null); // null = dialog closed
  // A running session belongs to whoever pressed Start; everyone else is a
  // spectator until it ends. Kept as one value so every gate agrees.
  const isSessionRunner = canRunSession(selectedEvent, user);
  // A future-dated session can't be run or attended yet, so the modal suppresses
  // Mark Attendance and Start for it — only Edit and Delete remain. One value so
  // every gate in the dialog agrees.
  const selectedIsFuture = selectedEvent
    ? isFutureSessionDate(selectedEvent.sessionDate || selectedEvent.start || selectedEvent.date)
    : false;
  // Same-day but not yet within the 1-hour window (a 10pm session opens at 9pm).
  // Gates conducting the session (Start / data collection) AND marking attendance
  // — but NOT creating/editing sessions. Future days are already covered by
  // selectedIsFuture; past/started sessions return false so back-fill is
  // unaffected. Built from date + startTime for a reliable scheduled datetime,
  // falling back to the event's start Date.
  const selectedTooEarly = selectedEvent && !selectedIsFuture ? isBeforeConductWindow(scheduledStartOf(selectedEvent)) : false;
  const [isDeletingSeries, setIsDeletingSeries] = useState(false);
  const [dateMismatchOpen, setDateMismatchOpen] = useState(false);

  // Attendance state
  const [attendance, setAttendance] = useState(null);
  const [attendanceReason, setAttendanceReason] = useState('');
  const [isMarkingAttendance, setIsMarkingAttendance] = useState(false);

  // Popover state for month view day click
  const [dayPopoverAnchor, setDayPopoverAnchor] = useState(null);
  const [selectedDaySessions, setSelectedDaySessions] = useState([]);
  const [selectedDayDate, setSelectedDayDate] = useState(null);

  const [newSession, setNewSession] = useState({
    childId: '',
    sessionType: 'individual',
    serviceId: '',
    startDateTime: new Date(),
    duration: 60,
    participants: [],
    notes: '',
    // Recurrence fields
    recurrence: 'none', // 'none', 'daily', 'weekly', 'monthly', 'weekdays', 'custom'
    recurrenceEndDate: null,
    recurrenceCount: 10,
    recurrenceEndType: 'count', // 'count', 'date', 'never'
    customDays: [] // [0-6] for custom recurrence (0=Sun, 6=Sat)
  });

  // Assigned children from API
  const [assignedChildren, setAssignedChildren] = useState([]);
  const [isLoadingChildren, setIsLoadingChildren] = useState(false);

  // Reference data (services, session types, statuses)
  const [refData, setRefData] = useState({ services: [], session_types: [], statuses: [] });
  const [isLoadingRefData, setIsLoadingRefData] = useState(false);

  // Session creation state
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // Collect Data in flight: overlay shown until the data sheet replaces the calendar.
  const [openingData, setOpeningData] = useState(false);
  const [, setImportHints] = useState(null);
  const [importBatchDialog, setImportBatchDialog] = useState({ open: false, events: [], fileName: '' });
  const [isImportingBatch, setIsImportingBatch] = useState(false);

  // Get unique children for filter dropdown (merge assigned + from sessions)
  const childrenList = useMemo(() => {
    const childMap = new Map();
    // Add assigned children first
    assignedChildren.forEach((child) => {
      childMap.set(String(child.id), {
        id: child.id,
        name: child.name,
        initials: child.initials
      });
    });
    // Add children from sessions (may include children no longer assigned)
    sessions.forEach((session) => {
      if (session.childId && !childMap.has(String(session.childId))) {
        childMap.set(String(session.childId), {
          id: session.childId,
          name: session.childName,
          initials: session.childInitials
        });
      }
    });
    return Array.from(childMap.values());
  }, [sessions, assignedChildren]);

  // Load assigned children and reference data on mount
  useEffect(() => {
    if (!isOfflineMode && user?.person_id) {
      loadAssignedChildren();
      loadReferenceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOfflineMode, user?.person_id]);

  const loadAssignedChildren = async () => {
    setIsLoadingChildren(true);
    try {
      const res = await listClients({ therapist_id: user.person_id, is_active: true });
      const clients = res?.items || res || [];
      setAssignedChildren(
        clients.map((c) => ({
          id: c.id,
          // `name` stays the LEGAL full name — other readers of assignedChildren rely on
          // it. What the pickers SHOW comes from childDisplayName, which reads
          // preferredName first and falls back to firstName.
          name: `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.client_code,
          preferredName: c.preferred_name,
          firstName: c.first_name,
          lastName: c.last_name,
          clientCode: c.client_code,
          initials: `${(c.first_name || '')[0] || ''}${(c.last_name || '')[0] || ''}`.toUpperCase()
        }))
      );
    } catch (error) {
      console.error('[Calendar] Failed to load assigned children:', error);
      setAssignedChildren([]);
    } finally {
      setIsLoadingChildren(false);
    }
  };

  const loadReferenceData = async () => {
    setIsLoadingRefData(true);
    try {
      const data = await getSessionReferenceData();
      setRefData(data);
    } catch (error) {
      console.error('[Calendar] Failed to load reference data:', error);
    } finally {
      setIsLoadingRefData(false);
    }
  };

  // Handle incoming session from dashboard or other pages
  useEffect(() => {
    if (location.state?.openSessionId) {
      const sessionId = location.state.openSessionId;
      const session = sessions.find((s) => s.id === sessionId);
      if (session) {
        // Open the session details modal
        const event = {
          ...session,
          id: session.id,
          childId: session.childId,
          childName: session.childName,
          childInitials: session.childInitials,
          program: session.program,
          start: new Date(`${session.date}T${session.startTime}:00`),
          end: new Date(`${session.date}T${session.endTime}:00`),
          duration: session.duration,
          status: session.status,
          sessionType: session.sessionType,
          notes: session.notes,
          sessionNotes: session.sessionNotes,
          children: session.children,
          collectedData: session.collectedData
        };
        setSelectedEvent(event);
        setModalOpen(true);
        // Clear the router state ONLY after the modal is open. window.history
        // .replaceState doesn't update React Router's location, so openSessionId
        // lingered and this effect re-fired on every `sessions` change (e.g. after
        // Start Session mutates the list) — reopening the dialog repeatedly.
        // navigate(..., {replace}) truly clears it; doing it inside the `if`
        // preserves the case where sessions load a beat after navigation.
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location.state, sessions, location.pathname, navigate]);

  // Get week start and end dates
  const getWeekBounds = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const start = new Date(d);
    start.setDate(d.getDate() - day);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  };

  // Get month bounds
  const getMonthBounds = (date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return { start, end };
  };

  // Get sessions for current view period (filtered by child if selected)
  const viewSessions = useMemo(() => {
    // Scheduled Sessions switched off: keep the calendar itself but surface no
    // events. Gating here — the single source every view (week/month/agenda) and
    // the ICS export reads from — hides sessions everywhere in one place.
    if (scheduledSessionsHidden) return [];
    const bounds = viewMode === 'week' ? getWeekBounds(currentDate) : getMonthBounds(currentDate);
    // Compare as local date strings to avoid UTC/local timezone mismatch
    const startStr = toLocalDateStr(bounds.start);
    const endStr = toLocalDateStr(bounds.end);
    return sessions.filter((session) => {
      const inDateRange = session.date >= startStr && session.date <= endStr;
      const matchesChild =
        selectedChild === 'all' ||
        String(session.childId) === String(selectedChild) ||
        (session.childrenIds && session.childrenIds.map(String).includes(String(selectedChild)));
      return inDateRange && matchesChild;
    });
  }, [sessions, currentDate, viewMode, selectedChild, scheduledSessionsHidden]);

  // Navigation handlers
  const handlePrevious = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      // Mobile week view shows a single day, so navigate day-by-day there
      newDate.setDate(newDate.getDate() - (downSM ? 1 : 7));
    } else {
      newDate.setMonth(newDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (downSM ? 1 : 7));
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Format date for header display
  const headerText = useMemo(() => {
    if (viewMode === 'week') {
      // Mobile week view is a single-day schedule — show that one day instead of a range
      if (downSM) {
        return currentDate.toLocaleDateString(dateLocaleTag, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
      }
      const { start, end } = getWeekBounds(currentDate);
      const startMonth = start.toLocaleDateString(dateLocaleTag, { month: 'short' });
      const endMonth = end.toLocaleDateString(dateLocaleTag, { month: 'short' });
      const year = end.getFullYear();
      if (startMonth === endMonth) {
        return `${startMonth} ${start.getDate()} - ${end.getDate()}, ${year}`;
      }
      return `${startMonth} ${start.getDate()} - ${endMonth} ${end.getDate()}, ${year}`;
    }
    return currentDate.toLocaleDateString(dateLocaleTag, { month: 'long', year: 'numeric' });
  }, [currentDate, viewMode, downSM, dateLocaleTag]);

  // Child color palette - dynamically assigned based on unique children
  const childColorPalette = [
    { main: '#1976d2', light: '#e3f2fd' }, // Blue
    { main: '#9c27b0', light: '#f3e5f5' }, // Purple
    { main: '#2e7d32', light: '#e8f5e9' }, // Green
    { main: '#ed6c02', light: '#fff3e0' }, // Orange
    { main: '#d32f2f', light: '#ffebee' }, // Red
    { main: '#0288d1', light: '#e1f5fe' }, // Light Blue
    { main: '#7b1fa2', light: '#f3e5f5' }, // Deep Purple
    { main: '#388e3c', light: '#e8f5e9' } // Teal
  ];

  const childColorMap = useMemo(() => {
    const map = {};
    childrenList.forEach((child, index) => {
      map[child.id] = childColorPalette[index % childColorPalette.length];
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childrenList]);

  // Get color based on child
  const getChildColor = (childId) => {
    return childColorMap[childId] || { main: '#757575', light: '#f5f5f5' };
  };

  // Status helpers. Chips read `displayStatus` (see transformSession) so a
  // scheduled session whose slot has passed reads "Missed" instead of
  // "Upcoming"; the start/data-entry actions still key off the raw `status`.
  const displayStatusOf = (session) => session?.displayStatus || session?.status;

  // A started-but-overdue session (displayStatus 'pending-submission') gets a soft
  // red treatment — thin red border + faint red wash — matching the supervisor
  // screens. Color lives inside the shorthand (a separate borderColor would be
  // reset to black). Spread AFTER the block's own border/bg props so it overrides.
  const pendingSubmissionBoxSx = (session) =>
    displayStatusOf(session) === 'pending-submission'
      ? {
          border: `1px solid ${alpha(theme.palette.error.main, 0.55)}`,
          borderLeft: `1px solid ${alpha(theme.palette.error.main, 0.55)}`,
          bgcolor: alpha(theme.palette.error.main, 0.08),
          '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.16) }
        }
      : null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in-progress':
        return 'warning';
      // Started but overdue for submission — red, distinct from in-progress.
      case 'pending-submission':
        return 'error';
      case 'upcoming':
        return 'info';
      case 'pending':
        return 'error';
      case 'no-show':
        return 'error';
      case 'missed':
        return 'warning';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return intl.formatMessage({ id: 'calendar.status.completed' });
      case 'in-progress':
        return intl.formatMessage({ id: 'calendar.status.inProgress' });
      case 'pending-submission':
        return intl.formatMessage({ id: 'calendar.status.pendingSubmission', defaultMessage: 'Pending Submission' });
      case 'upcoming':
        return intl.formatMessage({ id: 'calendar.status.upcoming' });
      case 'pending':
        return intl.formatMessage({ id: 'calendar.status.pending' });
      case 'no-show':
        return intl.formatMessage({ id: 'calendar.status.noShow' });
      case 'missed':
        return intl.formatMessage({ id: 'calendar.status.missed' });
      case 'cancelled':
        return intl.formatMessage({ id: 'calendar.status.cancelled' });
      default:
        return status;
    }
  };

  // Session colors based on child (not status)
  const getSessionBgColor = (session) => {
    const color = getChildColor(session.childId);
    return alpha(color.main, 0.15);
  };

  const getSessionBorderColor = (session) => {
    const color = getChildColor(session.childId);
    return color.main;
  };

  // Session click handler
  const handleSessionClick = (session) => {
    // Convert session to event format for modal
    const event = {
      ...session,
      id: session.id,
      childId: session.childId,
      childName: session.childName,
      childInitials: session.childInitials,
      program: session.program,
      start: new Date(`${session.date}T${session.startTime}:00`),
      end: new Date(`${session.date}T${session.endTime}:00`),
      duration: session.duration,
      status: session.status,
      sessionType: session.sessionType,
      notes: session.notes,
      sessionNotes: session.sessionNotes,
      children: session.children,
      collectedData: session.collectedData
    };
    setSelectedEvent(event);
    setModalOpen(true);

    // Fetch collected data for completed sessions
    if (session.status === 'completed' && session.id) {
      setIsLoadingCollectedData(true);
      setSessionCollectedData([]);
      getSessionData(session.id)
        .then((res) => {
          const items = res?.items || res || [];
          // Deduplicate: keep only the latest record per datasheet type
          const latestByType = new Map();
          items.forEach((dc) => {
            const typeKey = dc.datasheet_type_id || dc.datasheet_type?.id || dc.raw_data?.formType || dc.id;
            const existing = latestByType.get(typeKey);
            if (!existing || new Date(dc.collected_at) > new Date(existing.collected_at)) {
              latestByType.set(typeKey, dc);
            }
          });
          const deduped = Array.from(latestByType.values());
          const mapped = deduped.map((dc) => {
            const rd = dc.raw_data || {};
            const formType = rd.formType;
            let displayMode = 'summary';
            let current = 0,
              total = 0,
              percent = 0,
              summary = intl.formatMessage({ id: 'calendar.dataSummary.completed' });

            switch (formType) {
              case 'cold-probe':
              case 'maintenance-probe': {
                displayMode = 'progress';
                const targets = rd.targets || [];
                total = rd.completedCount ?? targets.filter((t) => t.response).length;
                current = rd.correctCount ?? targets.filter((t) => t.response === 'correct').length;
                percent = total > 0 ? Math.round((current / total) * 100) : 0;
                break;
              }
              case 'multi-trial': {
                displayMode = 'progress';
                current = rd.totalCorrect ?? 0;
                total = rd.totalCompleted ?? 0;
                percent = rd.accuracy ?? (total > 0 ? Math.round((current / total) * 100) : 0);
                break;
              }
              case 'daily-mand': {
                const dm = rd.totalMands ?? (rd.occurrences?.length || 0);
                const p = rd.promptedMands ?? 0;
                const i = rd.independentMands ?? 0;
                summary =
                  dm > 0
                    ? intl.formatMessage({ id: 'calendar.dataSummary.mandsCount' }, { total: dm, prompted: p, independent: i })
                    : intl.formatMessage({ id: 'calendar.dataSummary.noMands' });
                break;
              }
              case 'behavior-interval': {
                const tot = rd.totalIntervals ?? rd.data?.length ?? 0;
                const whole = rd.wholeCount ?? 0;
                const wp = rd.wholePercent ?? (tot > 0 ? Math.round((whole / tot) * 100) : 0);
                summary =
                  tot > 0
                    ? intl.formatMessage({ id: 'calendar.dataSummary.wholeIntervals' }, { percent: wp, whole, total: tot })
                    : intl.formatMessage({ id: 'calendar.dataSummary.noIntervals' });
                break;
              }
              case 'behavior-tracking': {
                const count = rd.totalOccurrences ?? rd.occurrences?.length ?? 0;
                summary = intl.formatMessage({ id: 'calendar.dataSummary.occurrencesRecorded' }, { count });
                break;
              }
              case 'abc': {
                const count = rd.totalRecords ?? rd.records?.length ?? 0;
                summary = intl.formatMessage({ id: 'calendar.dataSummary.incidentsRecorded' }, { count });
                break;
              }
              case 'injury-report':
                summary = intl.formatMessage({ id: 'calendar.dataSummary.injuryReportFiled' });
                break;
              case 'ot-data-sheet':
              case 'slp-data-sheet':
                summary = intl.formatMessage(
                  { id: 'calendar.dataSummary.goalsTracked', defaultMessage: '{count} goals tracked' },
                  { count: (rd.goals || []).length }
                );
                break;
              case 'toileting-program': {
                const recs = rd.records || [];
                const tpTotal = recs.length;
                const unprompted = recs.filter((r) => r.prompt === 'U').length;
                const accidents = recs.filter((r) => r.accident && r.accident !== 'none').length;
                summary =
                  tpTotal > 0
                    ? intl.formatMessage({ id: 'calendar.dataSummary.toiletingRecords' }, { total: tpTotal, unprompted, accidents })
                    : intl.formatMessage({ id: 'calendar.dataSummary.noRecords' });
                break;
              }
              case 'skill-tracking': {
                const targets = rd.targets || [];
                const mastered = rd.masteredTargets ?? targets.filter((t) => t.dateMastered).length;
                summary = intl.formatMessage({ id: 'calendar.dataSummary.targetsMastered' }, { mastered, total: targets.length });
                break;
              }
              case 'parent-training': {
                const count = rd.observations?.length ?? 0;
                summary = intl.formatMessage({ id: 'calendar.dataSummary.sessionsDocumented' }, { count });
                break;
              }
              case 'lesson-plan': {
                const count = rd.lessonPlans?.length ?? 0;
                summary = intl.formatMessage({ id: 'calendar.dataSummary.lessonPlansCreated' }, { count });
                break;
              }
              case 'session-plan': {
                const count = rd.sessionPlans?.length ?? 0;
                summary = intl.formatMessage({ id: 'calendar.dataSummary.sessionPlansCreated' }, { count });
                break;
              }
              default:
                // Fallback to top-level fields if available
                if (dc.total_trials > 0) {
                  displayMode = 'progress';
                  current = dc.correct_trials ?? 0;
                  total = dc.total_trials;
                  percent = dc.percentage != null ? Math.round(dc.percentage) : Math.round((current / total) * 100);
                }
            }

            return {
              type: dc.datasheet_type?.name?.toLowerCase().replace(/\s+/g, '-') || 'unknown',
              name: dc.datasheet_type?.name || dc.notes || intl.formatMessage({ id: 'calendar.dataSummary.dataEntry' }),
              completed: true,
              displayMode,
              current,
              total,
              percent,
              summary,
              // Keep the raw form data so the View Data dialog can render the
              // individual entries (targets, trials, intervals, etc.).
              formType,
              // Session notes live on the parent `notes` column (buildPayload strips
              // `notes` from raw_data), so surface it for the OT/SLP renderers that read
              // raw.notes. Prefer an in-raw_data value if one ever exists.
              rawData: { ...rd, notes: rd.notes ?? dc.notes ?? undefined },
              collectedAt: dc.collected_at
            };
          });
          setSessionCollectedData(mapped);
        })
        .catch((err) => {
          console.error('[Calendar] Failed to fetch collected data:', err);
          setSessionCollectedData([]);
        })
        .finally(() => setIsLoadingCollectedData(false));
    }
  };

  // Day click handler for month view — session creation disabled for therapist
  const handleDayClick = (event, date, daySessions) => {
    if (daySessions.length === 0) return; // no-op: therapists cannot create sessions
    setDayPopoverAnchor(event.currentTarget);
    setSelectedDaySessions(daySessions);
    setSelectedDayDate(date);
  };

  const handleCloseDayPopover = () => {
    setDayPopoverAnchor(null);
    setSelectedDaySessions([]);
    setSelectedDayDate(null);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEvent(null);
    setSessionCollectedData([]);
    setAttendance(null);
    setAttendanceReason('');
    setDateMismatchOpen(false);
  };

  // Format time helper
  const formatTime = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleTimeString(dateLocaleTag, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format date helper
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString(dateLocaleTag, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Check if a session's date matches today (local timezone)
  const isSessionToday = (event) => {
    if (!event) return true;
    const sessionDate = event.sessionDate || event.date;
    if (!sessionDate) return true;
    const sessionDateStr =
      typeof sessionDate === 'string' && sessionDate.length === 10 ? sessionDate : toLocalDateStr(new Date(sessionDate));
    return sessionDateStr === toLocalDateStr(new Date());
  };

  // Session action handlers
  // Human message for the 1-hour conduct window: names the scheduled start time
  // and the time the window opens (start − 1 hour). Uses formatTime so it reads in
  // the viewer's local time, matching the times shown elsewhere in the modal.
  const conductWindowMessage = (startValue) => {
    const start = startValue ? new Date(startValue) : null;
    const openFrom = start ? new Date(start.getTime() - 60 * 60 * 1000) : null;
    return `This session starts at ${formatTime(start)}. You can begin up to 1 hour before — please try again from ${formatTime(openFrom)}.`;
  };

  const handleStartSession = async () => {
    if (!selectedEvent) return;

    // A FUTURE-dated session is a hard block: data collection is not allowed until
    // its scheduled day (the backend rejects a future start too, and doStartSession
    // swallows API errors while advancing the UI — so a future start must never
    // reach it). This is distinct from the past-date "start anyway" warning below,
    // which stays valid for back-filling a missed/past session.
    if (isFutureSessionDate(selectedEvent.sessionDate || selectedEvent.date || selectedEvent.start)) {
      setSnackbar({
        open: true,
        message: 'This session is scheduled for a future date. You can start it on or after its scheduled date.',
        severity: 'warning'
      });
      return;
    }

    // Same-day but too early: a session can only be started within 1 hour of its
    // scheduled start time (a 10pm session opens at 9pm).
    const startAt = scheduledStartOf(selectedEvent);
    if (isBeforeConductWindow(startAt)) {
      setSnackbar({ open: true, message: conductWindowMessage(startAt), severity: 'warning' });
      return;
    }

    // Warn if session date is not today
    if (!isSessionToday(selectedEvent)) {
      setDateMismatchOpen(true);
      return;
    }

    await doStartSession();
  };

  const doStartSession = async () => {
    if (calReadOnly) return; // Full-Read: cannot start a session.
    setDateMismatchOpen(false);
    if (selectedEvent) {
      let apiSucceeded = false;
      try {
        // Call backend API to start the session
        if (!isOfflineMode) {
          await apiStartSession(selectedEvent.id);
          apiSucceeded = true;
        }
      } catch (error) {
        console.error('[Calendar] Failed to start session:', error);
      }

      // Advance the UI optimistically whether the API call succeeded or the
      // connection dropped mid-request. This MUST live outside the try above:
      // when the therapist is on the online route but has actually lost
      // connectivity, apiStartSession() throws, and if these updates sat after
      // the await they'd be skipped — leaving the session stuck on 'upcoming'
      // so the modal kept showing "Start Session" no matter how many times it
      // was pressed. The change is queued for sync in the block below.
      const startedAt = new Date().toISOString();
      // startedBy mirrors what the server just recorded. Without it canRunSession still
      // reads the pre-start value until the next reload, and whoever started the
      // session can be refused Collect Data and End Session on it.
      const startedBy = user?.person_id || null;
      setSessions((prev) => prev.map((s) => (s.id === selectedEvent.id ? { ...s, status: 'in-progress', startedAt, startedBy } : s)));
      setSelectedEvent((prev) => (prev ? { ...prev, status: 'in-progress', startedAt, startedBy } : prev));

      // Persist to IndexedDB and queue for sync when offline OR when API call failed
      if (isOfflineMode || !apiSucceeded) {
        try {
          await updateCachedSession(selectedEvent.id, { status: 'in-progress' });
          await saveSessionUpdate({
            sessionId: selectedEvent.id,
            updateType: SESSION_UPDATE_TYPES.START,
            status: 'in-progress',
            actualStartTime: new Date().toISOString(),
            therapistId: user?.id
          });
        } catch (error) {
          console.error('Error updating cached session:', error);
        }
      }
    }
  };

  const handleMarkAbsent = async () => {
    if (calReadOnly) return; // Full-Read: cannot change attendance/cancel.
    if (!selectedEvent || !attendance) return;
    setIsMarkingAttendance(true);
    let apiSucceeded = false;
    try {
      const newStatus = attendance === 'absent' ? 'no-show' : 'cancelled';
      const reason = attendanceReason.trim() || attendance;

      if (!isOfflineMode) {
        if (attendance === 'absent') {
          await markNoShow(selectedEvent.id, { cancellation_reason: reason });
        } else {
          await cancelSession(selectedEvent.id, { cancellation_reason: reason });
        }
        apiSucceeded = true;
      }

      // Update local state
      const updatedSessions = sessions.map((s) => (s.id === selectedEvent.id ? { ...s, status: newStatus } : s));
      setSessions(updatedSessions);

      // Persist to IndexedDB when offline OR when API call failed
      if (isOfflineMode || !apiSucceeded) {
        try {
          await updateCachedSession(selectedEvent.id, { status: newStatus });
          await saveSessionUpdate({
            sessionId: selectedEvent.id,
            updateType: attendance === 'absent' ? SESSION_UPDATE_TYPES.NO_SHOW : SESSION_UPDATE_TYPES.CANCEL,
            status: newStatus,
            cancellationReason: reason,
            therapistId: user?.id
          });
        } catch (error) {
          console.error('Error updating cached session:', error);
        }
      }

      handleCloseModal();
    } catch (error) {
      console.error('[Calendar] Failed to mark attendance:', error);
      // Fallback: queue for later sync if API failed
      if (!apiSucceeded) {
        const newStatus = attendance === 'absent' ? 'no-show' : 'cancelled';
        const reason = attendanceReason.trim() || attendance;
        try {
          await saveSessionUpdate({
            sessionId: selectedEvent.id,
            updateType: attendance === 'absent' ? SESSION_UPDATE_TYPES.NO_SHOW : SESSION_UPDATE_TYPES.CANCEL,
            status: newStatus,
            cancellationReason: reason,
            therapistId: user?.id
          });
        } catch (queueErr) {
          console.error('Error queuing session update fallback:', queueErr);
        }
      }
    } finally {
      setIsMarkingAttendance(false);
    }
  };

  const handleEndSession = async () => {
    if (calReadOnly) return; // Full-Read: cannot end/complete a session.
    if (selectedEvent) {
      let apiSucceeded = false;
      try {
        // Call backend API to end the session (include pre-session notes if any)
        if (!isOfflineMode) {
          let preNotes = selectedEvent.notes || '';
          // Parse in case notes is already JSON from a prior session
          try {
            const p = JSON.parse(preNotes);
            preNotes = p.preSessionNotes || preNotes;
          } catch {
            /* plain text */
          }
          let notesPayload = {};
          if (preNotes) {
            notesPayload = { notes: JSON.stringify({ preSessionNotes: preNotes }) };
          }
          await completeSession(selectedEvent.id, { actual_end: new Date().toISOString(), ...notesPayload });
          apiSucceeded = true;
        }

        // Update local state
        const updatedSessions = sessions.map((s) =>
          s.id === selectedEvent.id ? { ...s, status: 'completed', completedAt: new Date().toISOString() } : s
        );
        setSessions(updatedSessions);
      } catch (error) {
        console.error('[Calendar] Failed to end session:', error);
      }

      // Persist to IndexedDB and queue for sync when offline OR when API call failed
      // (handles Edge reporting online when internet is actually down)
      if (isOfflineMode || (!apiSucceeded && !isOfflineMode)) {
        try {
          await updateCachedSession(selectedEvent.id, { status: 'completed' });
          await saveSessionUpdate({
            sessionId: selectedEvent.id,
            updateType: SESSION_UPDATE_TYPES.END,
            status: 'completed',
            actualEndTime: new Date().toISOString(),
            therapistId: user?.id
          });
        } catch (error) {
          console.error('Error updating cached session:', error);
        }
      }
    }
    handleCloseModal();
  };

  const handleCollectData = async () => {
    if (selectedEvent) {
      // Data collection is blocked for future-dated sessions. The event can exist on
      // the calendar for a future day, but no data may be recorded until that day —
      // so don't even enter the data-collection screen. (Backend enforces this too.)
      if (isFutureSessionDate(selectedEvent.start)) {
        setSnackbar({
          open: true,
          message: 'Data cannot be collected for a future-dated session. You can start it on or after its scheduled date.',
          severity: 'warning'
        });
        handleCloseModal();
        return;
      }

      // Same-day but too early: data collection opens only within 1 hour of the
      // scheduled start time (a 10pm session opens at 9pm).
      const startAt = scheduledStartOf(selectedEvent);
      if (isBeforeConductWindow(startAt)) {
        setSnackbar({ open: true, message: conductWindowMessage(startAt), severity: 'warning' });
        handleCloseModal();
        return;
      }

      // From here the data sheet is on its way: cover the dialog so the calendar is
      // never shown while the note saves and the data-collection chunk loads. The
      // component unmounts on navigate, so the overlay is never lifted by hand.
      if (openingData) return;
      setOpeningData(true);

      // Extract plain-text pre-session notes (may be JSON from prior End Session)
      let preNotes = selectedEvent.notes || '';
      try {
        const p = JSON.parse(preNotes);
        preNotes = p.preSessionNotes || preNotes;
      } catch {
        /* plain text */
      }

      if (preNotes && !isOfflineMode) {
        try {
          await updateSession(selectedEvent.id, { notes: preNotes });
        } catch (err) {
          console.error('[Calendar] Failed to save pre-session notes:', err);
        }
      }

      // Navigate to appropriate data collection route based on mode.
      //
      // Online: enter WITHOUT pinning a sheet. The data-collection page selects the
      // first sheet that is switched ON in Features and Pages — so disabling e.g. Cold
      // Probe simply opens the session on the next available sheet, instead of the
      // route guard blocking the /cold-probe URL and ejecting the therapist to their
      // dashboard (which read as "the whole session won't load"). Offline keeps the
      // sheet in the URL because that page skips the tab-selection effect; offline is
      // unaffected by the switch anyway, as visibility is a live lookup.
      const basePath = isOfflineMode ? '/offline' : '/therapist';
      const dataCollectionPath = isOfflineMode
        ? `${basePath}/data-collection/${selectedEvent.childId}/cold-probe`
        : `${basePath}/data-collection/${selectedEvent.childId}`;
      navigate(dataCollectionPath, {
        state: {
          sessionDetails: {
            id: selectedEvent.id,
            sessionId: selectedEvent.id,
            childId: selectedEvent.childId,
            childName: selectedEvent.childName,
            program: selectedEvent.program,
            date: selectedEvent.start,
            startTime: formatTime(selectedEvent.start),
            endTime: formatTime(selectedEvent.end),
            duration: selectedEvent.duration,
            status: selectedEvent.status,
            sessionType: selectedEvent.sessionType,
            preSessionNotes: preNotes
          }
        }
      });
    }
    handleCloseModal();
  };

  const handleResumeData = () => {
    handleCollectData();
  };

  // Open an inline dialog that shows the detailed data collected during this
  // session. We deliberately don't navigate to /therapist/data-collection here
  // — earlier attempts to navigate to that page from the session-details modal
  // were getting swallowed (the route transition was racing with the modal's
  // own onClose-state-clearing), leaving the therapist back on the calendar
  // with nothing to look at. Showing the data inline avoids that entire class
  // of bug.
  const handleViewSessionData = () => {
    if (!selectedEvent) return;
    setViewDataOpen(true);
  };

  const handleCloseViewData = () => {
    setViewDataOpen(false);
  };

  const handleViewChild = () => {
    if (selectedEvent && selectedEvent.childId) {
      // Child profile not available offline, navigate to data collection instead
      if (isOfflineMode) {
        navigate(`/offline/data-collection/${selectedEvent.childId}/cold-probe`);
      } else {
        navigate(`/therapist/child/${selectedEvent.childId}`);
      }
    }
    handleCloseModal();
  };

  // Therapists cannot create sessions — cell clicks are no-ops
  const handleCellClick = () => {};

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
  };

  // Resolve a child hint (e.g. "AVKA1 (Arjun)") to a child record from childrenList
  const resolveChildFromHint = (hint) => {
    if (!hint) return null;
    const h = hint.toLowerCase();
    return (
      childrenList.find((c) => {
        const nameL = (c.name || '').toLowerCase();
        const codeL = (c.clientCode || '').toLowerCase();
        return nameL === h || codeL === h || nameL.includes(h) || h.includes(codeL);
      }) || null
    );
  };

  // ICS import handler
  const handleImportICS = (event) => {
    if (calReadOnly) return; // Full-Read: importing (creates sessions) is disabled.
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    if (!file.name.toLowerCase().endsWith('.ics')) {
      setSnackbar({ open: true, message: 'Invalid file — please select a .ics file', severity: 'error' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        if (!content.includes('BEGIN:VCALENDAR') || !content.includes('BEGIN:VEVENT')) {
          setSnackbar({ open: true, message: 'Invalid ICS format — no calendar events found', severity: 'error' });
          return;
        }
        const allParsed = parseAllICSEvents(content);
        if (allParsed.length === 0 || !allParsed[0].dtstart) {
          setSnackbar({ open: true, message: 'No valid events found in this ICS file', severity: 'error' });
          return;
        }
        if (allParsed.length > 1) {
          setImportBatchDialog({ open: true, events: allParsed, fileName: file.name });
          setSnackbar({ open: true, message: `Found ${allParsed.length} events in ${file.name}`, severity: 'info' });
          return;
        }
        // Single event — pre-fill create dialog
        const parsed = allParsed[0];
        let serviceId = refData?.services?.[0]?.id || '';
        if (parsed.programHint) {
          const hint = parsed.programHint.toLowerCase();
          const svc = (refData?.services || []).find((s) => s.name.toLowerCase().includes(hint));
          if (svc) serviceId = svc.id;
        }
        const matchedChild = resolveChildFromHint(parsed.childHint);
        setImportHints({
          fileName: file.name,
          summary: parsed.summary,
          childHint: parsed.childHint || null,
          programHint: parsed.programHint || null,
          childMatched: !!matchedChild
        });
        setNewSession((prev) => ({
          ...prev,
          childId: matchedChild?.id || '',
          serviceId,
          sessionType: parsed.typeHint?.toLowerCase().includes('group') ? 'group' : 'individual',
          startDateTime: parsed.dtstart,
          duration: parsed.duration || 60
        }));
        setAddDialogOpen(true);
        setSnackbar({ open: true, message: 'Event imported — review details and save', severity: 'success' });
      } catch (err) {
        console.error('[Therapist Calendar] Failed to parse ICS:', err);
        setSnackbar({ open: true, message: 'Failed to parse ICS file', severity: 'error' });
      }
    };
    reader.readAsText(file);
  };

  // Batch ICS import — create all events where child can be resolved
  const handleImportBatch = async () => {
    if (calReadOnly) return; // Full-Read backstop.
    if (!importBatchDialog.events.length) return;
    setIsImportingBatch(true);
    let created = 0;
    let skipped = 0;

    const scheduledStatus = (refData?.statuses || []).find((s) => (s.code || s.name || '').toLowerCase().includes('scheduled'));

    for (const parsed of importBatchDialog.events) {
      try {
        if (!parsed.dtstart) {
          skipped++;
          continue;
        }

        // Resolve child — required for individual sessions
        const matchedChild = resolveChildFromHint(parsed.childHint);
        if (!matchedChild) {
          skipped++;
          continue;
        }

        const isGroup = parsed.typeHint?.toLowerCase().includes('group');
        const sessionTypeObj =
          (refData?.session_types || []).find((t) =>
            isGroup ? (t.code || t.name || '').toLowerCase().includes('group') : !(t.code || t.name || '').toLowerCase().includes('group')
          ) || refData?.session_types?.[0];
        if (!sessionTypeObj || !scheduledStatus) {
          skipped++;
          continue;
        }

        let serviceId = refData?.services?.[0]?.id || '';
        if (parsed.programHint) {
          const svc = (refData?.services || []).find((s) => s.name.toLowerCase().includes(parsed.programHint.toLowerCase()));
          if (svc) serviceId = svc.id;
        }
        const endDT = parsed.dtend || new Date(parsed.dtstart.getTime() + (parsed.duration || 60) * 60000);
        await createSession({
          therapist_id: user?.person_id,
          client_id: matchedChild.id,
          session_type_id: sessionTypeObj.id,
          service_id: serviceId || null,
          session_date: toLocalDateStr(parsed.dtstart),
          scheduled_start: toLocalISOStr(parsed.dtstart),
          scheduled_end: toLocalISOStr(endDT),
          status_id: scheduledStatus.id,
          is_billable: true,
          is_makeup: false,
          notes: null
        });
        created++;
      } catch {
        skipped++;
      }
    }

    setIsImportingBatch(false);
    setImportBatchDialog({ open: false, events: [], fileName: '' });
    await loadSessions();
    setSnackbar({
      open: true,
      message:
        created > 0
          ? `${created} session${created !== 1 ? 's' : ''} imported${skipped > 0 ? ` · ${skipped} skipped (child not matched)` : ''}`
          : `Import failed — child names in the file didn't match any student. Check the Child: field in the ICS.`,
      severity: created > 0 ? 'success' : 'error'
    });
  };

  // Export menu anchor
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const EXPORT_RANGES = [
    { label: 'This week', key: 'week' },
    { label: 'Next 15 days', key: 'next15' },
    { label: 'This month', key: 'month' },
    { label: 'Next 2 months', key: 'next1' },
    { label: 'Next 3 months', key: 'next3' },
    { label: 'Next 6 months', key: 'next6' }
  ];

  const handleExportRange = async (rangeKey) => {
    setExportMenuAnchor(null);
    setIsExporting(true);

    try {
      const today = new Date();
      let startD, endD;

      switch (rangeKey) {
        case 'week': {
          const bounds = getWeekBounds(today);
          startD = bounds.start;
          endD = bounds.end;
          break;
        }
        case 'next15': {
          startD = new Date(today);
          endD = new Date(today);
          endD.setDate(endD.getDate() + 14);
          break;
        }
        case 'month': {
          startD = new Date(today.getFullYear(), today.getMonth(), 1);
          endD = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          break;
        }
        case 'next1': {
          startD = new Date(today.getFullYear(), today.getMonth(), 1);
          endD = new Date(today.getFullYear(), today.getMonth() + 2, 0);
          break;
        }
        case 'next3': {
          startD = new Date(today);
          endD = new Date(today);
          endD.setMonth(endD.getMonth() + 3);
          break;
        }
        case 'next6': {
          startD = new Date(today);
          endD = new Date(today);
          endD.setMonth(endD.getMonth() + 6);
          break;
        }
        default:
          return;
      }

      const childLabel =
        selectedChild !== 'all'
          ? (childrenList.find((c) => c.id === selectedChild)?.name || 'Child').replace(/[\s/]/g, '_')
          : 'All_Students';
      const filename = `Ayro_MyCalendar_${childLabel}_${toLocalDateStr(startD)}_${toLocalDateStr(endD)}.ics`;

      // Fetch the selected range from the API
      const params = {
        therapist_id: user?.person_id,
        start_date: toLocalDateStr(startD),
        end_date: toLocalDateStr(endD),
        page_size: 1000,
        ...(selectedChild !== 'all' ? { client_id: selectedChild } : {})
      };
      const res = await listSessions(params);
      const items = (res?.items || res || []).map(transformSession);

      if (items.length === 0) {
        setSnackbar({ open: true, message: 'No sessions found in the selected range', severity: 'warning' });
        return;
      }

      exportCalendarAsICS(items, filename);
      setSnackbar({ open: true, message: `Exported ${items.length} session${items.length !== 1 ? 's' : ''} as ICS`, severity: 'success' });
    } catch (err) {
      console.error('[Calendar] Export failed:', err);
      setSnackbar({ open: true, message: 'Export failed — please try again', severity: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  // Helper: generate recurrence dates from the start date
  const generateRecurrenceDates = (startDate, recurrence, endType, count, endDate, customDays) => {
    const dates = [];
    const maxDates = 365; // safety cap
    let current = new Date(startDate);

    const shouldStop = (idx) => {
      if (endType === 'never') return idx >= maxDates;
      if (endType === 'count') return idx >= count;
      if (endType === 'date' && endDate) return current > new Date(endDate);
      return idx >= maxDates;
    };

    let idx = 0;
    while (!shouldStop(idx)) {
      if (recurrence === 'daily') {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
      } else if (recurrence === 'weekly') {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 7);
      } else if (recurrence === 'monthly') {
        dates.push(new Date(current));
        current.setMonth(current.getMonth() + 1);
      } else if (recurrence === 'weekdays') {
        // Monday-Friday only
        if (current.getDay() >= 1 && current.getDay() <= 5) {
          dates.push(new Date(current));
        } else {
          // Don't count this towards idx
          current.setDate(current.getDate() + 1);
          continue;
        }
        current.setDate(current.getDate() + 1);
      } else if (recurrence === 'custom' && customDays.length > 0) {
        if (customDays.includes(current.getDay())) {
          dates.push(new Date(current));
        } else {
          current.setDate(current.getDate() + 1);
          continue;
        }
        current.setDate(current.getDate() + 1);
      } else {
        break;
      }
      idx++;
    }
    return dates;
  };

  const handleAddSession = async () => {
    if (!isValidSessionDuration(newSession.duration)) {
      setSnackbar({
        open: true,
        message: intl.formatMessage({ id: 'calendar.duration.rangeError' }, { min: MIN_SESSION_MINUTES, max: MAX_SESSION_MINUTES }),
        severity: 'warning'
      });
      return;
    }
    setIsCreatingSession(true);
    try {
      const startDate = new Date(newSession.startDateTime);

      // Find the session type and status UUIDs
      const sessionTypeCode = newSession.sessionType === 'group' ? 'group' : 'individual';
      const sessionType = refData.session_types.find((t) => t.code === sessionTypeCode) || refData.session_types[0];
      const scheduledStatus = refData.statuses.find((s) => s.code === 'scheduled') || refData.statuses[0];

      if (!sessionType || !scheduledStatus) {
        setSnackbar({ open: true, message: 'Reference data not loaded. Please try again.', severity: 'error' });
        setIsCreatingSession(false);
        return;
      }

      const clientId = newSession.sessionType === 'individual' ? newSession.childId : null;
      if (newSession.sessionType === 'individual' && !clientId) {
        setSnackbar({ open: true, message: 'Please select a child.', severity: 'warning' });
        setIsCreatingSession(false);
        return;
      }

      // Format datetime as local ISO string without timezone (for TIMESTAMP WITHOUT TIME ZONE columns)
      const toLocalISOStr = (d) => {
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      };

      // One key shared by every occurrence of this repeat, so the series can
      // later be edited or deleted as a unit. Null for a one-off session.
      const isRecurring = newSession.recurrence !== 'none';
      const seriesId = isRecurring ? newSeriesId() : null;
      const recurrenceRule = isRecurring
        ? clampRecurrenceRule(
            describeRecurrence({
              recurrence: newSession.recurrence,
              startDate,
              interval: newSession.recurrenceInterval,
              unit: newSession.recurrenceUnit,
              customDays: newSession.customDays
            })
          )
        : null;

      // Build base session payload
      const buildPayload = (sessionDate) => ({
        client_id: clientId,
        session_type_id: sessionType.id,
        service_id: newSession.serviceId || null,
        therapist_id: user.person_id,
        session_date: toLocalDateStr(sessionDate),
        scheduled_start: toLocalISOStr(sessionDate),
        scheduled_end: toLocalISOStr(new Date(sessionDate.getTime() + newSession.duration * 60000)),
        status_id: scheduledStatus.id,
        is_billable: true,
        is_makeup: false,
        notes: newSession.notes || null,
        series_id: seriesId,
        recurrence_rule: recurrenceRule
      });

      if (newSession.recurrence === 'none') {
        // Single session - create via sessions API
        await createSession(buildPayload(startDate));
        setSnackbar({ open: true, message: 'Session created successfully!', severity: 'success' });
      } else {
        // Recurring sessions - generate dates and create each
        const dates = generateRecurrenceDates(
          startDate,
          newSession.recurrence,
          newSession.recurrenceEndType,
          newSession.recurrenceCount,
          newSession.recurrenceEndDate,
          newSession.customDays
        );

        // Create sessions in batch
        let created = 0;
        let failed = 0;
        for (const d of dates) {
          // Keep same time, different date
          const sessionDate = new Date(d);
          sessionDate.setHours(startDate.getHours(), startDate.getMinutes(), 0, 0);
          try {
            await createSession(buildPayload(sessionDate));
            created++;
          } catch (err) {
            console.error('[Calendar] Failed to create session for date:', toLocalDateStr(d), err);
            failed++;
          }
        }
        const msg = `Created ${created} session${created !== 1 ? 's' : ''}` + (failed > 0 ? ` (${failed} failed)` : '');
        setSnackbar({ open: true, message: msg, severity: failed > 0 ? 'warning' : 'success' });
      }

      // Reload sessions from API
      await loadSessions();
      handleCloseAddDialog();
    } catch (error) {
      console.error('[Calendar] Failed to create session:', error);
      setSnackbar({ open: true, message: `Failed to create session: ${error.message || 'Unknown error'}`, severity: 'error' });
    } finally {
      setIsCreatingSession(false);
    }
  };

  // Delete session handlers
  const handleCloseDeleteDialog = () => {
    if (isDeletingSeries) return;
    setDeleteDialogOpen(false);
    setDeleteMode('single');
  };

  const handleDeleteSession = async () => {
    if (calReadOnly) return; // Full-Read: cannot delete sessions.
    if (!selectedEvent) return;
    setIsDeletingSeries(true);

    try {
      // Both modes route to the same endpoint now — the backend resolves
      // "series" via the scheduled_instance_id → template_id relationship,
      // which is more reliable than the previous time-of-day pattern match
      // and works for therapists deleting their own sessions without needing
      // the clinical.session.delete permission.
      const res = await apiDeleteSession(selectedEvent.id, { scope: deleteMode });
      setSnackbar({
        open: true,
        message: res?.message || 'Session deleted',
        severity: 'success'
      });
      await loadSessions();
    } catch (error) {
      console.error('[Calendar] Failed to delete session(s):', error);
      setSessions(sessions.filter((s) => s.id !== selectedEvent.id));
      setSnackbar({ open: true, message: 'Delete failed — removed locally as fallback', severity: 'warning' });
    } finally {
      setIsDeletingSeries(false);
    }

    setDeleteDialogOpen(false);
    setDeleteMode('single');
    handleCloseModal();
  };

  // ==================== WEEK VIEW ====================
  const renderWeekView = () => {
    // On mobile, collapse the 7-day grid to a single-day schedule (Teams-style) so it
    // fits the screen with no horizontal scroll. timeColSize + dayColSize must sum to 12.
    const isDayView = downSM;
    const timeColSize = isDayView ? 2 : 1.5;
    const dayColSize = isDayView ? 10 : 1.5;

    const { start } = getWeekBounds(currentDate);
    const weekDays = [];
    if (isDayView) {
      const day = new Date(currentDate);
      day.setHours(0, 0, 0, 0);
      weekDays.push(day);
    } else {
      for (let i = 0; i < 7; i++) {
        const day = new Date(start);
        day.setDate(start.getDate() + i);
        weekDays.push(day);
      }
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Group by DATE only. The per-day layout engine places each session by its
    // exact start time and duration, so a :30 start is honoured (no hour-snapping)
    // and a session's height reflects how long it runs.
    const sessionsByDate = {};
    viewSessions.forEach((session) => {
      if (!session.date || !session.startTime) return;
      if (!sessionsByDate[session.date]) sessionsByDate[session.date] = [];
      sessionsByDate[session.date].push(session);
    });

    return (
      <Box sx={{ overflow: 'auto' }}>
        <Box sx={{ minWidth: isDayView ? 'auto' : 800 }}>
          {/* Header row with day names */}
          <Grid container>
            <Grid size={{ xs: timeColSize }}>
              <Box sx={{ p: 1, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider', height: 60 }} />
            </Grid>
            {weekDays.map((day, index) => {
              const isToday = day.getTime() === today.getTime();
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              return (
                <Grid key={index} size={{ xs: dayColSize }}>
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: isWeekend ? 'grey.100' : 'grey.50',
                      borderBottom: 1,
                      borderLeft: 1,
                      borderColor: 'divider',
                      height: 60,
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography
                      variant="caption"
                      color={isToday ? 'primary.main' : isWeekend ? 'error.main' : 'text.secondary'}
                      fontWeight={isToday ? 600 : 400}
                    >
                      {intl.formatMessage({ id: `calendar.weekday.${['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][day.getDay()]}` })}
                    </Typography>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        minHeight: 32,
                        borderRadius: '50%',
                        bgcolor: isToday ? 'primary.main' : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        aspectRatio: '1 / 1'
                      }}
                    >
                      <Typography
                        variant="h5"
                        fontWeight={isToday ? 700 : 400}
                        color={isToday ? 'white' : isWeekend ? 'error.main' : 'text.primary'}
                      >
                        {day.getDate()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {/* Time grid body: fixed-height time gutter plus one relative column per
              day. Sessions are absolutely positioned by start time and sized by
              duration (Teams/Outlook style) via computeDayColumnLayout, with
              concurrent sessions packed side by side. Background hour cells stay as
              gridlines and the click-to-create target. */}
          <Grid container sx={{ alignItems: 'stretch' }}>
            <Grid size={{ xs: timeColSize }}>
              {timeSlots.map((time) => (
                <Box
                  key={time}
                  sx={{
                    p: 1,
                    bgcolor: 'grey.50',
                    borderBottom: 1,
                    borderColor: 'divider',
                    height: HOUR_HEIGHT,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {time.replace(/^0/, '').replace(':00', '')}
                    {parseInt(time) < 12 ? 'am' : 'pm'}
                  </Typography>
                </Box>
              ))}
            </Grid>
            {weekDays.map((day, dayIndex) => {
              const dateStr = toLocalDateStr(day);
              const isWeekend = day.getDay() === 0 || day.getDay() === 6;
              const isTodayColumn = day.getTime() === today.getTime();
              const dayEvents = computeDayColumnLayout(sessionsByDate[dateStr] || [], {
                hourHeight: HOUR_HEIGHT,
                startHour: WEEK_START_HOUR,
                endHour: WEEK_END_HOUR
              });

              return (
                <Grid key={dayIndex} size={{ xs: dayColSize }}>
                  <Box sx={{ position: 'relative', height: gridBodyHeight(HOUR_HEIGHT, WEEK_START_HOUR, WEEK_END_HOUR) }}>
                    {/* Background hour cells — gridlines and the empty-slot click target */}
                    {timeSlots.map((time) => (
                      <Box
                        key={time}
                        onClick={() => handleCellClick(day, time)}
                        sx={{
                          height: HOUR_HEIGHT,
                          borderBottom: 1,
                          borderLeft: 1,
                          borderColor: 'divider',
                          boxSizing: 'border-box',
                          bgcolor: isTodayColumn ? alpha(theme.palette.primary.main, 0.04) : isWeekend ? 'grey.50' : 'background.paper',
                          cursor: 'pointer',
                          transition: 'background-color 0.15s',
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) }
                        }}
                      />
                    ))}
                    {/* Sessions, positioned by start time and sized by duration */}
                    {dayEvents.map(({ session, topPx, heightPx, leftPct, widthPct, gapPx, compact }) => {
                      // "Narrow" == shares its column with a concurrent session, so
                      // apply the tightening the old side-by-side layout used.
                      const hasMultiple = widthPct < 99;
                      // Status icons fit while a card has at least half the column (two side by
                      // side); with three or more sharing a slot there is no room, so drop them.
                      const showStatusIcon = widthPct >= 45;
                      return (
                        <Tooltip key={session.id} title={`${session.startTime} - ${session.endTime} • ${session.program}`}>
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSessionClick(session);
                            }}
                            sx={{
                              position: 'absolute',
                              top: `${topPx}px`,
                              height: `${heightPx}px`,
                              left: `calc(${leftPct}% + ${gapPx}px)`,
                              width: `calc(${widthPct}% - ${gapPx * 2}px)`,
                              p: compact ? 0.25 : 0.75,
                              borderRadius: 1,
                              bgcolor: getSessionBgColor(session),
                              borderLeft: 3,
                              borderColor: getSessionBorderColor(session),
                              cursor: 'pointer',
                              overflow: 'hidden',
                              boxSizing: 'border-box',
                              '&:hover': {
                                boxShadow: 2,
                                zIndex: 3,
                                bgcolor: alpha(getSessionBorderColor(session), 0.25)
                              },
                              ...(displayStatusOf(session) === 'in-progress' && {
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  width: '25%',
                                  height: '2px',
                                  bgcolor: theme.palette.warning.main,
                                  animation: 'progressSlide 1.5s linear infinite'
                                }
                              }),
                              ...(pendingSubmissionBoxSx(session) || {})
                            }}
                          >
                            <Stack spacing={0.15}>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                {session.sessionType === 'group' ? (
                                  <TeamOutlined style={{ fontSize: '0.65rem', color: theme.palette.text.secondary, flexShrink: 0 }} />
                                ) : (
                                  <UserOutlined style={{ fontSize: '0.65rem', color: theme.palette.text.secondary, flexShrink: 0 }} />
                                )}
                                <Typography
                                  variant="caption"
                                  noWrap
                                  sx={{ flex: 1, fontWeight: 600, fontSize: hasMultiple ? '0.6rem' : '0.7rem', lineHeight: 1.3 }}
                                >
                                  {session.childLabel || session.childName.split('(')[0].trim()}
                                </Typography>
                                {showStatusIcon && session.status === 'completed' && (
                                  <CheckCircleOutlined style={{ fontSize: '0.75rem', color: theme.palette.success.main, flexShrink: 0 }} />
                                )}
                                {/* In-progress: pulsing dot so an active session is
                                    distinguishable from an upcoming one at a glance.
                                    Keyed on displayStatus so an overdue session drops
                                    the pulse and shows the red mark below instead. */}
                                {showStatusIcon && displayStatusOf(session) === 'in-progress' && (
                                  <Box
                                    component="span"
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: 'warning.main',
                                      flexShrink: 0,
                                      animation: 'sessionPulse 1.4s ease-in-out infinite',
                                      '@keyframes sessionPulse': {
                                        '0%, 100%': { opacity: 1, boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0.5)}` },
                                        '50%': { opacity: 0.4, boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0)}` }
                                      }
                                    }}
                                  />
                                )}
                                {/* Pending Submission: started but overdue — solid red dot. */}
                                {showStatusIcon && displayStatusOf(session) === 'pending-submission' && (
                                  <Box
                                    component="span"
                                    sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }}
                                  />
                                )}
                                {showStatusIcon && session.status === 'no-show' && (
                                  <CloseCircleOutlined style={{ fontSize: '0.75rem', color: theme.palette.error.main, flexShrink: 0 }} />
                                )}
                                {/* Missed is a derived state (scheduled window closed with
                                    nobody starting it), so it lives on displayStatus. */}
                                {showStatusIcon && displayStatusOf(session) === 'missed' && (
                                  <CloseCircleOutlined style={{ fontSize: '0.75rem', color: theme.palette.error.main, flexShrink: 0 }} />
                                )}
                              </Stack>
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{ fontSize: hasMultiple ? '0.55rem' : '0.65rem', color: 'text.secondary', lineHeight: 1.2 }}
                              >
                                {session.program}
                              </Typography>
                            </Stack>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>
    );
  };

  // ==================== MONTH VIEW ====================
  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Build calendar grid
    const calendarDays = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      calendarDays.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(new Date(year, month, day));
    }
    const remainingCells = 7 - (calendarDays.length % 7);
    if (remainingCells < 7) {
      for (let i = 0; i < remainingCells; i++) {
        calendarDays.push(null);
      }
    }

    // Group sessions by date
    const sessionsByDate = {};
    viewSessions.forEach((session) => {
      if (!sessionsByDate[session.date]) {
        sessionsByDate[session.date] = [];
      }
      sessionsByDate[session.date].push(session);
    });

    // Split into weeks
    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }

    return (
      <Box sx={{ minWidth: 700 }}>
        {/* Week header */}
        <Grid container sx={{ borderBottom: 1, borderColor: 'divider' }}>
          {['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'].map((dayKey, index) => (
            <Grid key={dayKey} size={{ xs: 12 / 7 }}>
              <Box sx={{ py: 1.5, textAlign: 'center', bgcolor: 'grey.50' }}>
                <Typography variant="body2" fontWeight={600} color={index === 0 || index === 6 ? 'error.main' : 'text.primary'}>
                  {intl.formatMessage({ id: `calendar.weekday.${dayKey}` })}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Calendar weeks */}
        {weeks.map((week, weekIndex) => (
          <Grid container key={weekIndex} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            {week.map((date, dayIndex) => {
              if (!date) {
                return (
                  <Grid key={dayIndex} size={{ xs: 12 / 7 }}>
                    <Box
                      sx={{
                        minHeight: 140,
                        bgcolor: 'grey.50',
                        borderRight: dayIndex < 6 ? 1 : 0,
                        borderColor: 'divider'
                      }}
                    />
                  </Grid>
                );
              }

              const dateStr = toLocalDateStr(date);
              const daySessions = sessionsByDate[dateStr] || [];
              const isToday = date.getTime() === today.getTime();
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              const hasSessions = daySessions.length > 0;

              return (
                <Grid key={dayIndex} size={{ xs: 12 / 7 }}>
                  <Box
                    onClick={(e) => handleDayClick(e, date, daySessions)}
                    sx={{
                      minHeight: 140,
                      p: 1.5,
                      borderRight: dayIndex < 6 ? 1 : 0,
                      borderColor: 'divider',
                      bgcolor: isWeekend ? 'grey.50' : 'background.paper',
                      cursor: hasSessions ? 'pointer' : 'default',
                      '&:hover': hasSessions ? { bgcolor: alpha(theme.palette.primary.main, 0.05) } : {},
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          minWidth: 32,
                          minHeight: 32,
                          borderRadius: '50%',
                          bgcolor: isToday ? 'primary.main' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          aspectRatio: '1 / 1'
                        }}
                      >
                        <Typography
                          variant="body1"
                          fontWeight={isToday ? 700 : 500}
                          color={isToday ? 'white' : isWeekend ? 'error.main' : 'text.primary'}
                        >
                          {date.getDate()}
                        </Typography>
                      </Box>
                    </Box>

                    {daySessions.length > 0 && (
                      <Stack spacing={0.75} sx={{ flex: 1 }}>
                        {daySessions.slice(0, 3).map((session) => (
                          <Box
                            key={session.id}
                            sx={{
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              bgcolor: getSessionBgColor(session),
                              borderLeft: 3,
                              borderColor: getSessionBorderColor(session),
                              ...(pendingSubmissionBoxSx(session) || {})
                            }}
                          >
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{ flex: 1, fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.4, minWidth: 0 }}
                              >
                                {session.startTime} {session.childLabel || session.childName.split('(')[0].trim()}
                              </Typography>
                              {/* Status icons — mirror the week view so completed / in-progress /
                                  no-show / missed sessions read the same in both views. */}
                              {session.status === 'completed' && (
                                <CheckCircleOutlined style={{ fontSize: '0.75rem', color: theme.palette.success.main, flexShrink: 0 }} />
                              )}
                              {displayStatusOf(session) === 'in-progress' && (
                                <Box
                                  component="span"
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: 'warning.main',
                                    flexShrink: 0,
                                    animation: 'sessionPulse 1.4s ease-in-out infinite',
                                    '@keyframes sessionPulse': {
                                      '0%, 100%': { opacity: 1, boxShadow: `0 0 0 0 ${alpha(theme.palette.warning.main, 0.5)}` },
                                      '50%': { opacity: 0.4, boxShadow: `0 0 0 3px ${alpha(theme.palette.warning.main, 0)}` }
                                    }
                                  }}
                                />
                              )}
                              {/* Pending Submission: started but overdue — solid red dot. */}
                              {displayStatusOf(session) === 'pending-submission' && (
                                <Box
                                  component="span"
                                  sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', flexShrink: 0 }}
                                />
                              )}
                              {session.status === 'no-show' && (
                                <CloseCircleOutlined style={{ fontSize: '0.75rem', color: theme.palette.error.main, flexShrink: 0 }} />
                              )}
                              {/* Missed is a derived state (scheduled window closed with nobody
                                  starting it), so it lives on displayStatus, not raw status. */}
                              {displayStatusOf(session) === 'missed' && (
                                <CloseCircleOutlined style={{ fontSize: '0.75rem', color: theme.palette.error.main, flexShrink: 0 }} />
                              )}
                            </Stack>
                          </Box>
                        ))}
                        {daySessions.length > 3 && (
                          <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                            {intl.formatMessage({ id: 'calendar.month.moreEvents' }, { count: daySessions.length - 3 })}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        ))}
      </Box>
    );
  };

  // ==================== AGENDA VIEW ====================
  const renderAgendaView = () => {
    const sessionsByDate = {};
    viewSessions.forEach((session) => {
      if (!sessionsByDate[session.date]) {
        sessionsByDate[session.date] = [];
      }
      sessionsByDate[session.date].push(session);
    });

    const sortedDates = Object.keys(sessionsByDate).sort();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (sortedDates.length === 0) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{intl.formatMessage({ id: 'calendar.agenda.noSessions' })}</Typography>
        </Box>
      );
    }

    return (
      <Stack spacing={2}>
        {sortedDates.map((dateStr) => {
          const date = new Date(dateStr);
          const isToday = date.getTime() === today.getTime();
          const daySessions = sessionsByDate[dateStr].sort((a, b) => a.startTime.localeCompare(b.startTime));

          return (
            <Box key={dateStr}>
              <Box
                sx={{
                  py: 1,
                  px: 2,
                  bgcolor: isToday ? alpha(theme.palette.primary.main, 0.1) : 'grey.100',
                  borderRadius: 1,
                  mb: 1
                }}
              >
                <Typography variant="subtitle2" fontWeight={600} color={isToday ? 'primary.main' : 'text.primary'}>
                  {date.toLocaleDateString(dateLocaleTag, { weekday: 'long', month: 'long', day: 'numeric' })}
                  {isToday && ` ${intl.formatMessage({ id: 'calendar.agenda.today' })}`}
                </Typography>
              </Box>

              <Stack spacing={1}>
                {daySessions.map((session) => (
                  <Paper
                    key={session.id}
                    elevation={0}
                    onClick={() => handleSessionClick(session)}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      borderLeft: 4,
                      borderColor: getSessionBorderColor(session),
                      bgcolor: getSessionBgColor(session),
                      '&:hover': {
                        boxShadow: 2
                      },
                      ...(pendingSubmissionBoxSx(session) || {})
                    }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      justifyContent="space-between"
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      gap={1}
                    >
                      <Stack direction="row" spacing={{ xs: 1, sm: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Box sx={{ minWidth: 80 }}>
                          <Typography variant="body2" fontWeight={600}>
                            {session.startTime} - {session.endTime}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {session.sessionType === 'group' ? (
                            <TeamOutlined style={{ color: theme.palette.text.secondary }} />
                          ) : (
                            <UserOutlined style={{ color: theme.palette.text.secondary }} />
                          )}
                          <Typography variant="body1" fontWeight={500}>
                            {session.childName}
                          </Typography>
                        </Stack>
                        <Chip label={session.program} size="small" variant="outlined" />
                      </Stack>
                      {!attendanceHidden && (
                        <Chip
                          label={getStatusLabel(displayStatusOf(session))}
                          size="small"
                          color={getStatusColor(displayStatusOf(session))}
                        />
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  };

  // Animation keyframes
  const progressAnimation = `
    @keyframes progressSlide {
      0% { transform: translateX(-100%); }
      100% { transform: translateX(400%); }
    }
  `;

  return (
    <Box>
      {/* Collect Data in flight: above the dialog's z-index, until the data sheet is up. */}
      <Backdrop open={openingData} sx={{ zIndex: (t) => t.zIndex.modal + 1, color: 'common.white' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <style>{progressAnimation}</style>

      {/* Page banner — Primary background (primary.main→dark), matching the hero
          banner other pages use, so branding's Primary role reaches this page too. */}
      <MainCard
        sx={{
          mb: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'primary.contrastText'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              bgcolor: alpha('#ffffff', 0.2)
            }}
          >
            <HeaderIcon style={{ fontSize: '1.6rem' }} />
          </Box>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700 }}>
              {intl.formatMessage({ id: 'calendar.title' })}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
              {intl.formatMessage({ id: 'calendar.pageDescription' })}
            </Typography>
          </Box>
        </Stack>
      </MainCard>

      {/* Header with Navigation and View Switcher */}
      <MainCard sx={{ mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ gap: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <input type="file" accept=".ics" id="therapist-ics-import" style={{ display: 'none' }} onChange={handleImportICS} />

              {/* Desktop (md+): original inline Import ICS + Export Calendar buttons */}
              <Button
                variant="outlined"
                startIcon={<UploadOutlined />}
                size="medium"
                onClick={() => document.getElementById('therapist-ics-import').click()}
                disabled={calReadOnly}
                sx={{ display: { xs: 'none', md: 'inline-flex' } }}
              >
                Import ICS
              </Button>
              <ButtonGroup variant="outlined" color="secondary" size="medium" sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                <Button
                  startIcon={isExporting ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlined />}
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  disabled={isExporting}
                >
                  Export Calendar
                </Button>
                <Button size="small" onClick={(e) => setExportMenuAnchor(e.currentTarget)} disabled={isExporting} sx={{ px: 0.5 }}>
                  <CaretDownOutlined />
                </Button>
              </ButtonGroup>
              <Menu anchorEl={exportMenuAnchor} open={Boolean(exportMenuAnchor)} onClose={() => setExportMenuAnchor(null)}>
                {EXPORT_RANGES.map((range) => (
                  <MenuItem key={range.key} onClick={() => handleExportRange(range.key)}>
                    {range.label}
                  </MenuItem>
                ))}
              </Menu>

              {/* Tablet / mobile (< md): Import + Export collapsed into an overflow (⋯) menu */}
              <IconButton
                onClick={(e) => setActionsMenuAnchor(e.currentTarget)}
                aria-label="Import / export"
                aria-haspopup="true"
                aria-expanded={Boolean(actionsMenuAnchor)}
                sx={(theme) => ({
                  display: { xs: 'inline-flex', md: 'none' },
                  border: '1px solid',
                  borderRadius: 1,
                  borderColor: actionsMenuAnchor ? 'primary.main' : 'divider',
                  color: actionsMenuAnchor ? 'primary.main' : 'text.secondary',
                  bgcolor: actionsMenuAnchor ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08), borderColor: 'primary.main' }
                })}
              >
                {isExporting ? <CircularProgress size={18} color="inherit" /> : <MoreOutlined />}
              </IconButton>
              <Menu anchorEl={actionsMenuAnchor} open={Boolean(actionsMenuAnchor)} onClose={() => setActionsMenuAnchor(null)}>
                <MenuItem
                  disabled={calReadOnly}
                  onClick={() => {
                    setActionsMenuAnchor(null);
                    document.getElementById('therapist-ics-import').click();
                  }}
                >
                  <UploadOutlined style={{ marginRight: 8 }} />
                  Import ICS
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block' }}>
                  Export Calendar
                </Typography>
                {EXPORT_RANGES.map((range) => (
                  <MenuItem
                    key={range.key}
                    disabled={isExporting}
                    onClick={() => {
                      setActionsMenuAnchor(null);
                      handleExportRange(range.key);
                    }}
                  >
                    <DownloadOutlined style={{ marginRight: 8 }} />
                    {range.label}
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', md: 'center' }}
            spacing={2}
          >
            {/* Navigation */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent={{ xs: 'space-between', sm: 'flex-start' }}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button variant="outlined" size="small" onClick={handleToday}>
                {intl.formatMessage({ id: 'calendar.navigation.today' })}
              </Button>
              {/* Arrows hug the date so the label can never overlap them on narrow screens */}
              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton onClick={handlePrevious} size="small">
                  <LeftOutlined />
                </IconButton>
                <Typography
                  variant="h5"
                  sx={{
                    minWidth: { xs: 'auto', sm: 200 },
                    textAlign: 'center',
                    fontSize: { xs: '0.95rem', sm: '1.25rem' },
                    whiteSpace: 'nowrap'
                  }}
                >
                  {headerText}
                </Typography>
                <IconButton onClick={handleNext} size="small">
                  <RightOutlined />
                </IconButton>
              </Stack>
            </Stack>

            {/* Child Filter and View Switcher */}
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ width: { xs: '100%', sm: 'auto' } }}>
              {/* Child Filter */}
              <FormControl size="small" sx={{ minWidth: 150, width: { xs: '100%', sm: 'auto' } }}>
                <Select value={selectedChild} onChange={(e) => setSelectedChild(e.target.value)} displayEmpty>
                  <MenuItem value="all">{intl.formatMessage({ id: 'calendar.filter.allChildren' })}</MenuItem>
                  {childrenList.map((child) => (
                    <MenuItem key={child.id} value={child.id}>
                      {child.name.split('(')[0].trim()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedChild !== 'all' && (
                <Button size="small" variant="text" color="error" onClick={() => setSelectedChild('all')}>
                  {intl.formatMessage({ id: 'calendar.filter.clear' })}
                </Button>
              )}

              {/* View Switcher — full width on mobile, the 3 options share the row equally */}
              <ButtonGroup
                variant="outlined"
                size="small"
                sx={{ width: { xs: '100%', sm: 'auto' }, '& .MuiButtonGroup-grouped': { flex: { xs: 1, sm: 'none' } } }}
              >
                {viewOptions.map((option) => {
                  const Icon = option.icon;
                  return (
                    <Tooltip title={intl.formatMessage({ id: option.labelId })} key={option.value}>
                      <Button variant={viewMode === option.value ? 'contained' : 'outlined'} onClick={() => setViewMode(option.value)}>
                        <Icon style={{ fontSize: '1.2rem' }} />
                      </Button>
                    </Tooltip>
                  );
                })}
              </ButtonGroup>
            </Stack>
          </Stack>
        </Stack>
      </MainCard>

      {/* Calendar View */}
      <MainCard sx={{ overflow: 'visible' }} content={false}>
        <Box sx={{ p: { xs: 1.5, sm: 2.5 }, overflow: 'auto' }}>
          {viewMode === 'week' && renderWeekView()}
          {viewMode === 'month' && renderMonthView()}
          {viewMode === 'agenda' && renderAgendaView()}
        </Box>
      </MainCard>

      {/* Day Sessions Popover (for month view) */}
      <Popover
        open={Boolean(dayPopoverAnchor)}
        anchorEl={dayPopoverAnchor}
        onClose={handleCloseDayPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box sx={{ p: 2, minWidth: 250, maxWidth: 350 }}>
          {selectedDayDate && (
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
              {selectedDayDate.toLocaleDateString(dateLocaleTag, { weekday: 'long', month: 'long', day: 'numeric' })}
            </Typography>
          )}
          <Stack spacing={1}>
            {selectedDaySessions.map((session) => (
              <Paper
                key={session.id}
                elevation={0}
                onClick={() => {
                  handleSessionClick(session);
                  handleCloseDayPopover();
                }}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  borderLeft: 3,
                  borderColor: getSessionBorderColor(session),
                  bgcolor: getSessionBgColor(session),
                  '&:hover': { boxShadow: 1 },
                  ...(pendingSubmissionBoxSx(session) || {})
                }}
              >
                <Stack spacing={0.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight={600}>
                      {session.startTime} - {session.endTime}
                    </Typography>
                    {!attendanceHidden && (
                      <Chip
                        label={getStatusLabel(displayStatusOf(session))}
                        size="small"
                        color={getStatusColor(displayStatusOf(session))}
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    )}
                  </Stack>
                  <Typography variant="body2">{session.childName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {session.program}
                  </Typography>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Box>
      </Popover>

      {/* Session Detail Modal */}
      {/* `disableEnforceFocus` / `disableEscapeKeyDown` are lifted WHILE the diary is over
          this dialog: MUI's focus trap would otherwise pull focus out of the diary's own
          textarea on every keystroke, and Escape would close the dialog underneath instead
          of the diary on top. The dialog stays mounted and untouched, which is the point —
          the therapist comes back to the attendance and the note they left behind. */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        // One width for every state. The narrow 'sm' dated from a footer with two actions in
        // it; an in-progress session now carries five, and 600px is what wrapped them into a
        // cramped two-line block.
        maxWidth="md"
        fullWidth
        fullScreen={downSM}
        disableEnforceFocus={diaryOpen}
        disableEscapeKeyDown={diaryOpen}
        slots={{ transition: PopupTransition }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">{intl.formatMessage({ id: 'calendar.modal.sessionDetails' })}</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseOutlined />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Card variant="outlined">
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                {/* Status Chip - wraps cleanly on narrow phones (gap, not spacing). */}
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
                    {!attendanceHidden && (
                      <Chip
                        label={getStatusLabel(displayStatusOf(selectedEvent))}
                        color={getStatusColor(displayStatusOf(selectedEvent))}
                        size="small"
                      />
                    )}
                    <Chip
                      label={
                        selectedEvent.sessionType === 'group'
                          ? intl.formatMessage({ id: 'calendar.sessionType.group' })
                          : intl.formatMessage({ id: 'calendar.sessionType.individual' })
                      }
                      icon={selectedEvent.sessionType === 'group' ? <TeamOutlined /> : <UserOutlined />}
                      size="small"
                      variant="outlined"
                    />
                  </Stack>
                </Box>

                <Divider sx={{ mb: 2.5 }} />

                {/* Two-column layout for completed sessions */}
                <Grid container spacing={3}>
                  {/* Left Column - Session Info */}
                  <Grid size={{ xs: 12, md: selectedEvent.status === 'completed' || selectedEvent.status === 'upcoming' ? 6 : 12 }}>
                    <Stack spacing={2.5}>
                      {/* Child + Program on same row */}
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5}>
                        {/* Child Info */}
                        <Box sx={{ flex: 1 }}>
                          {selectedEvent.sessionType === 'group' ? (
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                              <TeamOutlined style={{ fontSize: '1.5rem', color: theme.palette.primary.main }} />
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {intl.formatMessage({ id: 'calendar.modal.participants' })}
                                </Typography>
                                <Typography variant="h6">{selectedEvent.childName}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {selectedEvent.children && selectedEvent.children.join(', ')}
                                </Typography>
                              </Box>
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={2} alignItems="center">
                              <UserOutlined style={{ fontSize: '1.5rem', color: theme.palette.primary.main }} />
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {intl.formatMessage({ id: 'calendar.modal.child' })}
                                </Typography>
                                <Typography variant="h6">{selectedEvent.childDisplay || selectedEvent.childName}</Typography>
                              </Box>
                            </Stack>
                          )}
                        </Box>

                        {/* Program */}
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <CalendarOutlined style={{ fontSize: '1.5rem', color: theme.palette.primary.main }} />
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                {intl.formatMessage({ id: 'calendar.modal.program' })}
                              </Typography>
                              <Typography variant="body1">{selectedEvent.program}</Typography>
                            </Box>
                          </Stack>
                        </Box>
                      </Stack>

                      {/* Date & Time */}
                      <Stack direction="row" spacing={2} alignItems="flex-start">
                        <ClockCircleOutlined style={{ fontSize: '1.5rem', color: theme.palette.primary.main }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {intl.formatMessage({ id: 'calendar.modal.dateTime' })}
                          </Typography>
                          <Typography variant="body1">{formatDate(selectedEvent.start)}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            <strong>{intl.formatMessage({ id: 'calendar.modal.scheduled' })}</strong> {formatTime(selectedEvent.start)} -{' '}
                            {formatTime(selectedEvent.end)} ({selectedEvent.duration} {intl.formatMessage({ id: 'common.minutes' })})
                          </Typography>
                          {selectedEvent.actualStartTime && selectedEvent.actualEndTime && (
                            <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                              <strong>{intl.formatMessage({ id: 'calendar.modal.actual' })}</strong> {selectedEvent.actualStartTime} -{' '}
                              {selectedEvent.actualEndTime}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                    </Stack>
                  </Grid>

                  {/* Right Column - Data Collected (only for completed sessions) */}
                  {selectedEvent.status === 'completed' && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          height: { xs: 'auto', md: 200 },
                          maxHeight: { xs: 250, md: 200 },
                          pl: { xs: 0, md: 3 },
                          borderLeft: { xs: 'none', md: `1px solid ${theme.palette.divider}` }
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, flexShrink: 0 }}>
                          {intl.formatMessage({ id: 'calendar.modal.dataCollected' })}
                        </Typography>
                        {isLoadingCollectedData ? (
                          <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({ id: 'calendar.modal.loadingData' })}
                          </Typography>
                        ) : sessionCollectedData.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            {intl.formatMessage({ id: 'calendar.modal.noDataCollected' })}
                          </Typography>
                        ) : (
                          <Stack spacing={2} sx={{ overflowY: 'auto', flex: 1, pr: 1 }}>
                            {sessionCollectedData.map((data, idx) => {
                              const progressColor = data.percent >= 80 ? 'success' : data.percent >= 50 ? 'warning' : 'error';
                              return (
                                <Box key={`${data.type}-${idx}`}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                                    <Typography variant="body2" fontWeight={500}>
                                      {data.name}
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      {data.displayMode === 'progress' ? (
                                        <>
                                          <Typography variant="body2" color="text.secondary">
                                            {data.current}/{data.total}
                                          </Typography>
                                          <Chip
                                            label={`${data.percent}%`}
                                            size="small"
                                            color={progressColor}
                                            sx={{
                                              height: 22,
                                              fontSize: '0.7rem',
                                              fontWeight: 600,
                                              minWidth: 48
                                            }}
                                          />
                                        </>
                                      ) : (
                                        <Chip
                                          label={intl.formatMessage({ id: 'calendar.modal.dataCompleted' })}
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                          sx={{
                                            height: 22,
                                            fontSize: '0.7rem',
                                            fontWeight: 600
                                          }}
                                        />
                                      )}
                                    </Stack>
                                  </Stack>
                                  {data.displayMode === 'progress' ? (
                                    <Box
                                      sx={{
                                        height: 6,
                                        borderRadius: 3,
                                        bgcolor: alpha(theme.palette.grey[500], 0.12),
                                        overflow: 'hidden'
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          height: '100%',
                                          width: `${data.percent}%`,
                                          borderRadius: 3,
                                          bgcolor: `${progressColor}.main`,
                                          transition: 'width 0.3s ease'
                                        }}
                                      />
                                    </Box>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      {data.summary}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </Box>
                    </Grid>
                  )}

                  {/* Right Column - Attendance (only for upcoming sessions). For a
                      future-dated session it is shown but DISABLED — attendance can't
                      be marked before the day. */}
                  {selectedEvent.status === 'upcoming' && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box
                        sx={{
                          height: '100%',
                          pl: { xs: 0, md: 3 },
                          borderLeft: { xs: 'none', md: `1px solid ${theme.palette.divider}` }
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                          {intl.formatMessage({ id: 'calendar.modal.markAttendance' })}
                        </Typography>
                        <ToggleButtonGroup
                          value={attendance}
                          exclusive
                          disabled={selectedIsFuture || selectedTooEarly}
                          onChange={(e, val) => {
                            setAttendance(val);
                            setAttendanceReason('');
                          }}
                          fullWidth
                          orientation="vertical"
                          size="small"
                        >
                          <ToggleButton value="present" sx={{ py: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" width="100%">
                              <CheckCircleOutlined style={{ fontSize: '1.25rem', color: theme.palette.success.main }} />
                              <Typography variant="body2">{intl.formatMessage({ id: 'calendar.attendance.present' })}</Typography>
                            </Stack>
                          </ToggleButton>
                          <ToggleButton value="absent" sx={{ py: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" width="100%">
                              <CloseCircleOutlined style={{ fontSize: '1.25rem', color: theme.palette.error.main }} />
                              <Typography variant="body2">{intl.formatMessage({ id: 'calendar.attendance.absent' })}</Typography>
                            </Stack>
                          </ToggleButton>
                          <ToggleButton value="excused" sx={{ py: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" width="100%">
                              <QuestionCircleOutlined style={{ fontSize: '1.25rem', color: theme.palette.warning.main }} />
                              <Typography variant="body2">{intl.formatMessage({ id: 'calendar.attendance.excused' })}</Typography>
                            </Stack>
                          </ToggleButton>
                          <ToggleButton value="not-applicable" sx={{ py: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center" width="100%">
                              <MinusCircleOutlined style={{ fontSize: '1.25rem', color: theme.palette.grey[400] }} />
                              <Typography variant="body2">{intl.formatMessage({ id: 'calendar.attendance.notApplicable' })}</Typography>
                            </Stack>
                          </ToggleButton>
                        </ToggleButtonGroup>

                        {selectedIsFuture && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {intl.formatMessage({
                              id: 'calendar.attendance.futureDisabled',
                              defaultMessage: 'Attendance can be marked on or after the session date.'
                            })}
                          </Typography>
                        )}

                        {!selectedIsFuture && selectedTooEarly && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {intl.formatMessage(
                              {
                                id: 'calendar.attendance.tooEarlyDisabled',
                                defaultMessage: 'This session opens 1 hour before it starts — from {openFrom}.'
                              },
                              { openFrom: formatTime(new Date(scheduledStartOf(selectedEvent).getTime() - 60 * 60 * 1000)) }
                            )}
                          </Typography>
                        )}

                        {/* Optional reason text field for absent/excused */}
                        {(attendance === 'absent' || attendance === 'excused') && (
                          <TextField
                            fullWidth
                            size="small"
                            label={intl.formatMessage({ id: 'calendar.attendance.reasonLabel' })}
                            placeholder={
                              attendance === 'absent'
                                ? intl.formatMessage({ id: 'calendar.attendance.absentPlaceholder' })
                                : intl.formatMessage({ id: 'calendar.attendance.excusedPlaceholder' })
                            }
                            value={attendanceReason}
                            onChange={(e) => setAttendanceReason(e.target.value)}
                            multiline
                            rows={2}
                            sx={{ mt: 2 }}
                          />
                        )}
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {/* Pre-Session Notes - Only for the person running the session */}
                {selectedEvent.status === 'in-progress' && isSessionRunner && (
                  <Box sx={{ mt: 2.5 }}>
                    <Divider sx={{ mb: 2 }} />
                    <SpeechTextField
                      fullWidth
                      required
                      label={intl.formatMessage({ id: 'calendar.modal.preSessionNotes' })}
                      multiline
                      minRows={2}
                      maxRows={downSM ? 4 : 6}
                      value={(() => {
                        const raw = selectedEvent.notes || '';
                        try {
                          const p = JSON.parse(raw);
                          return p.preSessionNotes || raw;
                        } catch {
                          return raw;
                        }
                      })()}
                      onChange={(e) => {
                        const updatedSessions = sessions.map((s) => (s.id === selectedEvent.id ? { ...s, notes: e.target.value } : s));
                        setSessions(updatedSessions);
                        setSelectedEvent({ ...selectedEvent, notes: e.target.value });
                      }}
                      placeholder={intl.formatMessage({ id: 'calendar.modal.preSessionNotesPlaceholder' })}
                    />
                  </Box>
                )}

                {/* Show notes for completed sessions — parsed from JSON or plain text */}
                {selectedEvent.status === 'completed' &&
                  selectedEvent.notes &&
                  (() => {
                    let preNotes = '';
                    let completionNotes = '';
                    try {
                      const parsed = JSON.parse(selectedEvent.notes);
                      preNotes = parsed.preSessionNotes || '';
                      completionNotes = parsed.completionNotes || '';
                    } catch {
                      // Plain text — show as pre-session notes
                      preNotes = selectedEvent.notes;
                    }
                    return (
                      <>
                        {preNotes && (
                          <Box sx={{ mt: 2.5 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({ id: 'calendar.modal.preSessionNotesFromParent' })}
                            </Typography>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 1.5,
                                mt: 0.5,
                                bgcolor: alpha(theme.palette.warning.main, 0.08),
                                borderRadius: 1,
                                borderLeft: `3px solid ${theme.palette.warning.main}`
                              }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {preNotes}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                        {completionNotes && (
                          <Box sx={{ mt: 2.5 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({ id: 'calendar.modal.sessionNotesFromTherapist' })}
                            </Typography>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 1.5,
                                mt: 0.5,
                                bgcolor: alpha(theme.palette.info.main, 0.08),
                                borderRadius: 1,
                                borderLeft: `3px solid ${theme.palette.info.main}`
                              }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {completionNotes}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                      </>
                    );
                  })()}
              </CardContent>

              <Divider />

              {/* `useFlexGap` is what makes `spacing` a real gap rather than a margin on each
                  child: margins only separate along the main axis, so a row that wrapped had
                  its second line sitting flush against the first. */}
              <CardActions sx={{ px: 2, pb: 2, pt: 1.5 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap" width="100%">
                  {/* Start / attendance actions are suppressed for a future-dated
                      session, and for a same-day session before its 1-hour window
                      opens — leaving only Edit and Delete below. The window covers
                      both conducting the session and marking attendance. */}
                  {selectedEvent.status === 'upcoming' && !selectedIsFuture && !selectedTooEarly && (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        size="medium"
                        startIcon={<PlayCircleOutlined />}
                        onClick={handleStartSession}
                        disabled={calReadOnly || attendance !== 'present'}
                        sx={ACTION_SX}
                      >
                        {intl.formatMessage({ id: 'calendar.action.startSession' })}
                      </Button>
                      {(attendance === 'absent' || attendance === 'excused' || attendance === 'not-applicable') && (
                        <Button
                          variant="contained"
                          color="error"
                          size="medium"
                          startIcon={<CloseCircleOutlined />}
                          onClick={handleMarkAbsent}
                          disabled={calReadOnly || isMarkingAttendance}
                          sx={ACTION_SX}
                        >
                          {isMarkingAttendance
                            ? intl.formatMessage({ id: 'calendar.action.marking' })
                            : attendance === 'absent'
                              ? intl.formatMessage({ id: 'calendar.action.markAbsent' })
                              : attendance === 'excused'
                                ? intl.formatMessage({ id: 'calendar.action.markExcused' })
                                : intl.formatMessage({ id: 'calendar.action.markNA' })}
                        </Button>
                      )}
                    </>
                  )}

                  {/* A running session belongs to whoever started it. If a
                      supervisor started this one, the therapist watches it as
                      In Progress but cannot collect against it or end it — the
                      backend refuses either way, so the tooltip says why
                      rather than letting the click fail. */}
                  {/* One tooltip wrapper EACH. Stack spaces its direct children, and a
                      `display: contents` span generates no box for that margin to land
                      on, so sharing one wrapper left these two buttons flush against
                      each other and against the action before them. The span itself
                      stays because a disabled button fires no pointer events and the
                      tooltip needs something that does. */}
                  {selectedEvent.status === 'in-progress' && (
                    <>
                      <Tooltip title={runnerBlockedReason(selectedEvent, user)}>
                        <Box component="span" sx={{ ...ACTION_SX, display: 'inline-flex' }}>
                          <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="medium"
                            startIcon={openingData ? <CircularProgress size={16} color="inherit" /> : <FileTextOutlined />}
                            onClick={handleCollectData}
                            disabled={openingData || !isSessionRunner || !selectedEvent.notes || selectedEvent.notes.trim() === ''}
                          >
                            {intl.formatMessage({ id: 'calendar.action.collectData' })}
                          </Button>
                        </Box>
                      </Tooltip>
                      <Tooltip title={runnerBlockedReason(selectedEvent, user)}>
                        <Box component="span" sx={{ ...ACTION_SX, display: 'inline-flex' }}>
                          <Button
                            fullWidth
                            variant="contained"
                            color="error"
                            size="medium"
                            startIcon={<StopOutlined />}
                            onClick={handleEndSession}
                            disabled={calReadOnly || !isSessionRunner || !selectedEvent.notes || selectedEvent.notes.trim() === ''}
                          >
                            {intl.formatMessage({ id: 'calendar.action.endSession' })}
                          </Button>
                        </Box>
                      </Tooltip>
                    </>
                  )}

                  {selectedEvent.status === 'pending' && (
                    <Button
                      variant="contained"
                      color="warning"
                      size="medium"
                      startIcon={openingData ? <CircularProgress size={16} color="inherit" /> : <EditOutlined />}
                      onClick={handleResumeData}
                      sx={ACTION_SX}
                    >
                      {intl.formatMessage({ id: 'calendar.action.enterData' })}
                    </Button>
                  )}

                  {selectedEvent.status === 'completed' && (
                    <Tooltip title={isOfflineMode ? intl.formatMessage({ id: 'calendar.action.viewDataUnavailableOffline' }) : ''}>
                      <Box component="span" sx={{ ...ACTION_SX, display: 'inline-flex' }}>
                        <Button
                          fullWidth
                          variant="contained"
                          color="primary"
                          size="medium"
                          startIcon={<FileTextOutlined />}
                          onClick={handleViewSessionData}
                          disabled={isOfflineMode}
                        >
                          {intl.formatMessage({ id: 'calendar.action.viewData' })}
                        </Button>
                      </Box>
                    </Tooltip>
                  )}

                  {/* THE DIARY, BEFORE THE SESSION. What the home has written since the last
                      visit is the one thing a therapist wants in front of them while marking
                      attendance and writing the pre-session note — and afterwards it is where
                      they answer the caregiver. Opens over this dialog; needs the network, so
                      it says why it cannot when offline, the way View Data does. */}
                  {selectedEvent.sessionType !== 'group' && selectedEvent.childId && !diaryHidden && (
                    <Tooltip title={isOfflineMode ? intl.formatMessage({ id: 'calendar.action.diaryUnavailableOffline' }) : ''}>
                      <Box component="span" sx={{ ...ACTION_SX, display: 'inline-flex' }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="primary"
                          size="medium"
                          startIcon={<MessageOutlined />}
                          onClick={() => setDiaryOpen(true)}
                          disabled={isOfflineMode}
                        >
                          {intl.formatMessage({ id: 'calendar.action.communicationDiary' })}
                        </Button>
                      </Box>
                    </Tooltip>
                  )}

                  {selectedEvent.sessionType !== 'group' && (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="medium"
                      startIcon={<UserOutlined />}
                      onClick={handleViewChild}
                      sx={ACTION_SX}
                    >
                      {intl.formatMessage({ id: 'calendar.action.childProfile' })}
                    </Button>
                  )}

                  {/* Editing and deleting a session are supervisor actions, not the
                      therapist's. A therapist marks attendance, runs the session and
                      collects data; rescheduling or removing a session stays with the
                      supervisor's calendar. */}
                </Stack>
              </CardActions>
            </Card>
          )}
        </DialogContent>
      </Dialog>

      {/* Mounted outside the dialog, so nothing about the dialog's layout or its transition
          applies to a screen that is meant to fill the window. */}
      {diaryOpen && selectedEvent?.childId && <CommunicationDiaryTab childId={selectedEvent.childId} onClose={() => setDiaryOpen(false)} />}

      {/* Add Session Dialog */}
      <Dialog open={isAddDialogOpen} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth slots={{ transition: PopupTransition }}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h4">{intl.formatMessage({ id: 'calendar.addDialog.title' })}</Typography>
            <IconButton onClick={handleCloseAddDialog} size="small">
              <CloseOutlined />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3} sx={{ mt: 2 }}>
              {/* Session Type */}
              <FormControl fullWidth>
                <InputLabel>{intl.formatMessage({ id: 'calendar.addDialog.sessionType' })}</InputLabel>
                <Select
                  value={newSession.sessionType}
                  label={intl.formatMessage({ id: 'calendar.addDialog.sessionType' })}
                  onChange={(e) => setNewSession({ ...newSession, sessionType: e.target.value })}
                >
                  <MenuItem value="individual">{intl.formatMessage({ id: 'calendar.addDialog.individualSession' })}</MenuItem>
                  <MenuItem value="group">{intl.formatMessage({ id: 'calendar.addDialog.groupSession' })}</MenuItem>
                </Select>
              </FormControl>

              {/* Child / Participants - from assigned children API */}
              {newSession.sessionType === 'individual' ? (
                <FormControl fullWidth>
                  <InputLabel>{intl.formatMessage({ id: 'calendar.addDialog.child' })}</InputLabel>
                  <Select
                    value={newSession.childId}
                    label={intl.formatMessage({ id: 'calendar.addDialog.child' })}
                    onChange={(e) => setNewSession({ ...newSession, childId: e.target.value })}
                    disabled={isLoadingChildren}
                  >
                    {isLoadingChildren ? (
                      <MenuItem disabled>
                        <em>Loading assigned children...</em>
                      </MenuItem>
                    ) : assignedChildren.length === 0 ? (
                      <MenuItem disabled>
                        <em>No children assigned to you</em>
                      </MenuItem>
                    ) : (
                      assignedChildren.map((child) => (
                        <MenuItem key={child.id} value={child.id}>
                          {childDisplayName(child)} {child.clientCode ? `(${child.clientCode})` : ''}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              ) : (
                <FormControl fullWidth>
                  <InputLabel>{intl.formatMessage({ id: 'calendar.addDialog.participants' })}</InputLabel>
                  <Select
                    multiple
                    value={newSession.participants}
                    label={intl.formatMessage({ id: 'calendar.addDialog.participants' })}
                    onChange={(e) => setNewSession({ ...newSession, participants: e.target.value })}
                    disabled={isLoadingChildren}
                  >
                    {assignedChildren.map((child) => (
                      <MenuItem key={child.id} value={child.id}>
                        {childDisplayName(child)} {child.clientCode ? `(${child.clientCode})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Service / Program - from reference data API */}
              <FormControl fullWidth>
                <InputLabel>Service / Program</InputLabel>
                <Select
                  value={newSession.serviceId}
                  label="Service / Program"
                  onChange={(e) => setNewSession({ ...newSession, serviceId: e.target.value })}
                  disabled={isLoadingRefData}
                >
                  {isLoadingRefData ? (
                    <MenuItem disabled>
                      <em>Loading services...</em>
                    </MenuItem>
                  ) : refData.services.length === 0 ? (
                    <MenuItem disabled>
                      <em>No services available</em>
                    </MenuItem>
                  ) : (
                    refData.services.map((service) => (
                      <MenuItem key={service.id} value={service.id}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {service.color && (
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: service.color, flexShrink: 0 }} />
                          )}
                          <span>{service.name}</span>
                        </Stack>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {/* Start Date & Time */}
              <DateTimePicker
                label={intl.formatMessage({ id: 'calendar.addDialog.startDateTime' })}
                value={newSession.startDateTime}
                onChange={(newValue) => setNewSession({ ...newSession, startDateTime: newValue })}
                slotProps={{ textField: { fullWidth: true } }}
              />

              {/* Duration */}
              <TextField
                fullWidth
                label={intl.formatMessage({ id: 'calendar.addDialog.duration' })}
                type="number"
                value={newSession.duration}
                onChange={(e) => setNewSession({ ...newSession, duration: parseDurationInput(e.target.value) })}
                error={!isValidSessionDuration(newSession.duration)}
                helperText={
                  isValidSessionDuration(newSession.duration)
                    ? ''
                    : intl.formatMessage({ id: 'calendar.duration.rangeError' }, { min: MIN_SESSION_MINUTES, max: MAX_SESSION_MINUTES })
                }
                slotProps={{ htmlInput: { min: MIN_SESSION_MINUTES, max: MAX_SESSION_MINUTES, step: 15 } }}
              />

              {/* Recurrence */}
              <FormControl fullWidth>
                <InputLabel>Recurrence</InputLabel>
                <Select
                  value={newSession.recurrence}
                  label="Recurrence"
                  onChange={(e) => setNewSession({ ...newSession, recurrence: e.target.value })}
                >
                  <MenuItem value="none">Does not repeat</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekdays">Every weekday (Mon-Fri)</MenuItem>
                  <MenuItem value="weekly">
                    Weekly on{' '}
                    {newSession.startDateTime
                      ? ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][
                          new Date(newSession.startDateTime).getDay()
                        ]
                      : 'selected day'}
                  </MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="custom">Custom...</MenuItem>
                </Select>
              </FormControl>

              {/* Custom days selection */}
              {newSession.recurrence === 'custom' && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Repeat on
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                      <ToggleButton
                        key={day}
                        value={idx}
                        selected={newSession.customDays.includes(idx)}
                        onChange={() => {
                          const days = newSession.customDays.includes(idx)
                            ? newSession.customDays.filter((d) => d !== idx)
                            : [...newSession.customDays, idx];
                          setNewSession({ ...newSession, customDays: days });
                        }}
                        size="small"
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '50% !important',
                          border: '1px solid',
                          borderColor: 'divider',
                          '&.Mui-selected': {
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            '&:hover': { bgcolor: 'primary.dark' }
                          }
                        }}
                      >
                        {day[0]}
                      </ToggleButton>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Recurrence end condition */}
              {newSession.recurrence !== 'none' && (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    Ends
                  </Typography>
                  <Stack spacing={1.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={newSession.recurrenceEndType === 'never'}
                            onChange={() => setNewSession({ ...newSession, recurrenceEndType: 'never' })}
                            size="small"
                          />
                        }
                        label="Never"
                        sx={{ minWidth: 100 }}
                      />
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={newSession.recurrenceEndType === 'count'}
                            onChange={() => setNewSession({ ...newSession, recurrenceEndType: 'count' })}
                            size="small"
                          />
                        }
                        label="After"
                        sx={{ minWidth: 100 }}
                      />
                      <TextField
                        type="number"
                        size="small"
                        value={newSession.recurrenceCount}
                        onChange={(e) =>
                          setNewSession({ ...newSession, recurrenceCount: parseInt(e.target.value) || 1, recurrenceEndType: 'count' })
                        }
                        slotProps={{ htmlInput: { min: 1, max: 365 } }}
                        sx={{ width: 80 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        sessions
                      </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={newSession.recurrenceEndType === 'date'}
                            onChange={() => setNewSession({ ...newSession, recurrenceEndType: 'date' })}
                            size="small"
                          />
                        }
                        label="On date"
                        sx={{ minWidth: 100 }}
                      />
                      <DateTimePicker
                        value={newSession.recurrenceEndDate}
                        onChange={(v) => setNewSession({ ...newSession, recurrenceEndDate: v, recurrenceEndType: 'date' })}
                        slotProps={{
                          textField: { size: 'small', sx: { flex: 1 } }
                        }}
                        views={['year', 'month', 'day']}
                        format="MM/dd/yyyy"
                      />
                    </Stack>
                  </Stack>

                  {/* Preview count */}
                  {newSession.recurrence !== 'none' && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                      {(() => {
                        const dates = generateRecurrenceDates(
                          newSession.startDateTime || new Date(),
                          newSession.recurrence,
                          newSession.recurrenceEndType,
                          newSession.recurrenceCount,
                          newSession.recurrenceEndDate,
                          newSession.customDays
                        );
                        return `${dates.length} session${dates.length !== 1 ? 's' : ''} will be created`;
                      })()}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Notes */}
              <TextField
                fullWidth
                label="Pre-Session Notes (optional)"
                multiline
                rows={3}
                value={newSession.notes}
                onChange={(e) => setNewSession({ ...newSession, notes: e.target.value })}
                placeholder="Add any notes for this session..."
              />
            </Stack>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseAddDialog} color="secondary" disabled={isCreatingSession}>
            {intl.formatMessage({ id: 'calendar.addDialog.cancel' })}
          </Button>
          <Button
            onClick={handleAddSession}
            variant="contained"
            color="primary"
            disabled={
              isCreatingSession ||
              (!newSession.childId && newSession.sessionType === 'individual') ||
              (newSession.recurrence === 'custom' && newSession.customDays.length === 0) ||
              !isValidSessionDuration(newSession.duration)
            }
            startIcon={isCreatingSession ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {isCreatingSession
              ? 'Creating...'
              : newSession.recurrence !== 'none'
                ? `Create ${(() => {
                    const dates = generateRecurrenceDates(
                      newSession.startDateTime || new Date(),
                      newSession.recurrence,
                      newSession.recurrenceEndType,
                      newSession.recurrenceCount,
                      newSession.recurrenceEndDate,
                      newSession.customDays
                    );
                    return `${dates.length} Session${dates.length !== 1 ? 's' : ''}`;
                  })()}`
                : intl.formatMessage({ id: 'calendar.addDialog.addSession' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch ICS Import Dialog */}
      <Dialog
        open={importBatchDialog.open}
        onClose={() => !isImportingBatch && setImportBatchDialog({ open: false, events: [], fileName: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <UploadOutlined style={{ color: '#1976d2' }} />
              <Typography variant="h5">Import Calendar Events</Typography>
            </Stack>
            <IconButton size="small" onClick={() => !isImportingBatch && setImportBatchDialog({ open: false, events: [], fileName: '' })}>
              <CloseOutlined />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {(() => {
            const matched = importBatchDialog.events.filter((ev) => resolveChildFromHint(ev.childHint));
            const skipped = importBatchDialog.events.length - matched.length;
            if (matched.length === 0) {
              return (
                <Alert severity="warning">
                  <Typography variant="body2">
                    None of the {importBatchDialog.events.length} events in <strong>{importBatchDialog.fileName}</strong> matched any
                    student in your list. Nothing will be imported.
                  </Typography>
                </Alert>
              );
            }
            return (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>
                      {matched.length} of {importBatchDialog.events.length} sessions
                    </strong>{' '}
                    matched students in your list
                    {skipped > 0 ? ` · ${skipped} unmatched event${skipped !== 1 ? 's' : ''} hidden` : ''}.
                  </Typography>
                </Alert>
                <Stack spacing={1} sx={{ maxHeight: 360, overflow: 'auto' }}>
                  {matched.map((ev, idx) => {
                    const matchedChild = resolveChildFromHint(ev.childHint);
                    const dateLabel = ev.dtstart
                      ? ev.dtstart.toLocaleDateString(dateLocaleTag, { weekday: 'short', month: 'short', day: 'numeric' })
                      : 'Unknown date';
                    const timeLabel = ev.dtstart
                      ? ev.dtstart.toLocaleTimeString(dateLocaleTag, { hour: 'numeric', minute: '2-digit', hour12: true })
                      : '';
                    return (
                      <Box
                        key={idx}
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'success.light',
                          bgcolor: 'success.lighter'
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Box flex={1}>
                            <Typography variant="subtitle2" fontWeight={600} noWrap>
                              {ev.summary || `Session ${idx + 1}`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              <CalendarOutlined style={{ marginRight: 4 }} />
                              {dateLabel} · {timeLabel}
                              {ev.duration ? ` · ${ev.duration} min` : ''}
                            </Typography>
                            <Typography variant="caption" display="block" color="success.dark">
                              {matchedChild.name} ✓
                            </Typography>
                            {ev.programHint && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                Program: {ev.programHint}
                              </Typography>
                            )}
                          </Box>
                          <Chip label="Ready" size="small" color="success" sx={{ ml: 1, flexShrink: 0 }} />
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setImportBatchDialog({ open: false, events: [], fileName: '' })} disabled={isImportingBatch}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleImportBatch}
            disabled={isImportingBatch || importBatchDialog.events.every((ev) => !resolveChildFromHint(ev.childHint))}
            startIcon={isImportingBatch ? <CircularProgress size={16} color="inherit" /> : <UploadOutlined />}
          >
            {isImportingBatch
              ? 'Importing...'
              : `Import ${importBatchDialog.events.filter((ev) => resolveChildFromHint(ev.childHint)).length} Sessions`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for session creation feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Edit Session Dialog — scoped to one occurrence or the series. The
          therapist owns the session, so the therapist field stays fixed. */}
      <EditSessionDialog
        open={Boolean(editScope)}
        session={selectedEvent}
        scope={editScope || 'single'}
        services={refData?.services || []}
        onClose={() => setEditScope(null)}
        onSaved={(message, severity = 'success') => {
          setSnackbar({ open: true, message, severity });
          loadSessions();
          handleCloseModal();
        }}
      />

      {/* Delete Confirmation Dialog. What to delete was chosen in the Delete
          dropdown, so this only confirms it. */}
      <Dialog open={isDeleteDialogOpen} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth slots={{ transition: PopupTransition }}>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <DeleteOutlined style={{ color: '#d32f2f' }} />
            <Typography variant="h5">{deleteMode === 'single' ? 'Delete Session' : 'Delete Recurring Sessions'}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {deleteMode === 'single' &&
                `Only ${
                  selectedEvent
                    ? new Date(selectedEvent.date).toLocaleDateString(dateLocaleTag, {
                        weekday: 'long',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })
                    : 'this session'
                } will be removed.`}
              {deleteMode === 'following' && 'This session and every later session in the series will be removed.'}
              {deleteMode === 'series' && 'Every future session in this recurring series will be removed.'}
            </Typography>
            {deleteMode !== 'single' && (
              <Alert severity="warning" sx={{ py: 0.5 }}>
                <Typography variant="caption">
                  Sessions that already ran, or that have data collected, are kept. This cannot be undone from the UI.
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCloseDeleteDialog} color="secondary" disabled={isDeletingSeries}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSession}
            variant="contained"
            color="error"
            disabled={isDeletingSeries}
            startIcon={isDeletingSeries ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlined />}
          >
            {isDeletingSeries ? 'Deleting…' : deleteMode === 'single' ? 'Delete Session' : 'Delete Sessions'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Data — inline dialog that shows the detailed entries collected
          during a completed session. */}
      <Dialog open={viewDataOpen} onClose={handleCloseViewData} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack>
              <Typography variant="h5">{intl.formatMessage({ id: 'calendar.viewData.title', defaultMessage: 'Session Data' })}</Typography>
              {selectedEvent && (
                <Typography variant="caption" color="text.secondary">
                  {selectedEvent.childDisplay || selectedEvent.childName} ·{' '}
                  {selectedEvent.start
                    ? new Date(selectedEvent.start).toLocaleString(dateLocaleTag, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })
                    : ''}
                </Typography>
              )}
            </Stack>
            <IconButton onClick={handleCloseViewData} size="small" aria-label="Close">
              <CloseOutlined />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {isLoadingCollectedData ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {intl.formatMessage({ id: 'calendar.modal.loadingData' })}
            </Typography>
          ) : sessionCollectedData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {intl.formatMessage({ id: 'calendar.modal.noDataCollected' })}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {sessionCollectedData.map((data, idx) => {
                const rd = data.rawData || {};
                const progressColor = data.percent >= 80 ? 'success' : data.percent >= 50 ? 'warning' : 'error';
                return (
                  <Paper key={`${data.type}-${idx}`} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {data.name}
                        </Typography>
                        {data.displayMode === 'progress' ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              {data.current}/{data.total}
                            </Typography>
                            <Chip label={`${data.percent}%`} size="small" color={progressColor} />
                          </Stack>
                        ) : (
                          <Chip
                            label={data.summary || intl.formatMessage({ id: 'calendar.dataSummary.completed' })}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Stack>

                      {/* Every collected sheet gets a uniform "View details"
                          button. The drill-down dialog (below) dispatches on
                          formType to render the right detail layout. */}
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          {data.summary}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setDetailEntry({ name: data.name, formType: data.formType, rawData: rd })}
                        >
                          {intl.formatMessage({ id: 'calendar.viewData.viewDetails', defaultMessage: 'View details' })}
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewData}>{intl.formatMessage({ id: 'common.close' })}</Button>
        </DialogActions>
      </Dialog>

      {/* Detail dialog — driven by detailEntry.formType. One dialog handles
          every data sheet (cold-probe, multi-trial, behavior-interval,
          behavior-tracking, daily-mand, abc, injury-report, etc.) by
          dispatching to the right renderer below. */}
      <Dialog open={Boolean(detailEntry)} onClose={() => setDetailEntry(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h5">
              {detailEntry?.name || intl.formatMessage({ id: 'calendar.viewData.detailsTitle', defaultMessage: 'Details' })}
            </Typography>
            <IconButton onClick={() => setDetailEntry(null)} size="small" aria-label="Close">
              <CloseOutlined />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            if (!detailEntry) return null;
            const raw = detailEntry.rawData || {};
            const formType = detailEntry.formType;

            // ---------- Cold Probe / Maintenance Probe ----------
            if (formType === 'cold-probe' || formType === 'maintenance-probe') {
              const targets = raw.targets || [];
              const completed = targets.filter((t) => t.response).length;
              const correct = targets.filter((t) => t.response === 'correct').length;
              const pct = completed > 0 ? Math.round((correct / completed) * 100) : 0;
              return (
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={intl.formatMessage(
                        { id: 'calendar.viewData.targetsCompleted', defaultMessage: '{done} of {total} targets' },
                        { done: completed, total: targets.length }
                      )}
                    />
                    <Chip
                      size="small"
                      color={pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error'}
                      label={`${correct} correct (${pct}%)`}
                    />
                  </Stack>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={40}>#</TableCell>
                          <TableCell>{intl.formatMessage({ id: 'form.label.target' })}</TableCell>
                          <TableCell align="center" width={130}>
                            {intl.formatMessage({ id: 'form.coldProbe.promptLevel', defaultMessage: 'Prompt' })}
                          </TableCell>
                          <TableCell align="center" width={130}>
                            {intl.formatMessage({ id: 'form.label.response' })}
                          </TableCell>
                          <TableCell>{intl.formatMessage({ id: 'form.label.notes' })}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {targets.map((t, i) => (
                          <TableRow key={t.targetId || i}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{t.targetName || t.name || `Target ${i + 1}`}</TableCell>
                            <TableCell align="center">
                              {toPromptCodeArray(t.promptLevel).length ? (
                                <Chip size="small" label={toPromptCodeArray(t.promptLevel).join(', ')} variant="outlined" />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                color={t.response === 'correct' ? 'success' : t.response === 'incorrect' ? 'error' : 'default'}
                                label={t.response || '—'}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {t.notes || '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {raw.sessionNotes && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {intl.formatMessage({ id: 'form.label.sessionNotes', defaultMessage: 'Session Notes' })}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {raw.sessionNotes}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              );
            }

            // ---------- Multi Trial ----------
            if (formType === 'multi-trial') {
              const targets = raw.targets || [];
              const trialCount = Math.max(...targets.map((t) => (t.trials || []).length), 0);
              return (
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={`${raw.totalCompleted ?? 0} trials`} />
                    <Chip
                      size="small"
                      color={(raw.accuracy ?? 0) >= 80 ? 'success' : (raw.accuracy ?? 0) >= 50 ? 'warning' : 'error'}
                      label={`${raw.totalCorrect ?? 0} correct (${raw.accuracy ?? 0}%)`}
                    />
                  </Stack>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={40}>#</TableCell>
                          <TableCell>{intl.formatMessage({ id: 'form.label.target' })}</TableCell>
                          <TableCell align="center" width={110}>
                            {intl.formatMessage({ id: 'form.coldProbe.promptLevel', defaultMessage: 'Prompt' })}
                          </TableCell>
                          {Array.from({ length: trialCount }, (_, i) => (
                            <TableCell key={i} align="center" sx={{ minWidth: 36 }}>
                              T{i + 1}
                            </TableCell>
                          ))}
                          <TableCell align="center">
                            {intl.formatMessage({ id: 'form.multiTrial.total', defaultMessage: 'Total' })}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {targets.map((t, i) => (
                          <TableRow key={t.targetId || i}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>{t.targetName || t.name || `Target ${i + 1}`}</TableCell>
                            <TableCell align="center">
                              {toPromptCodeArray(t.promptLevel).length ? (
                                <Chip size="small" label={toPromptCodeArray(t.promptLevel).join(', ')} variant="outlined" />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            {(t.trials || []).map((trial, j) => (
                              <TableCell key={j} align="center">
                                <Box
                                  component="span"
                                  sx={{
                                    fontWeight: 700,
                                    color: trial === 'correct' ? 'success.main' : trial === 'incorrect' ? 'error.main' : 'text.disabled'
                                  }}
                                >
                                  {trial === 'correct' ? '✓' : trial === 'incorrect' ? '✗' : trial === 'no-response' ? '—' : ''}
                                </Box>
                              </TableCell>
                            ))}
                            <TableCell align="center">
                              <Typography variant="caption" color="text.secondary">
                                {t.correct}/{t.completed}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              );
            }

            // ---------- Behavior Interval ----------
            // Shared with SessionDataDialog: every recording on the sheet, with names.
            if (formType === 'behavior-interval') {
              return <BehaviorIntervalRecordingsView raw={raw} />;
            }

            // ---------- Behavior Tracking ----------
            if (formType === 'behavior-tracking') {
              const behaviors = raw.behaviors || [];
              const occurrences = raw.occurrences || [];
              // Setting can live at session level (raw.setting) or per-occurrence; prefer the
              // session-level value, else the distinct settings tagged on occurrences.
              const occurrenceSettings = [...new Set(occurrences.map((o) => o.setting).filter(Boolean))];
              const displaySettings = raw.setting ? [raw.setting] : occurrenceSettings;
              return (
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {displaySettings.length > 0 ? (
                      displaySettings.map((s) => (
                        <Chip
                          key={s}
                          icon={<EnvironmentOutlined />}
                          label={`Setting: ${s}`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ))
                    ) : (
                      <Chip icon={<EnvironmentOutlined />} label="Setting: not recorded" size="small" variant="outlined" />
                    )}
                  </Stack>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{intl.formatMessage({ id: 'dataCollection.field.behavior' })}</TableCell>
                          <TableCell align="center">Frequency</TableCell>
                          <TableCell align="center">Duration</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {behaviors.map((b, i) => (
                          <TableRow key={i}>
                            <TableCell>{b.behaviorName}</TableCell>
                            <TableCell align="center">{b.frequencyCount ?? 0}</TableCell>
                            <TableCell align="center">
                              {b.totalDuration > 0
                                ? `${Math.floor(b.totalDuration / 60)}:${String(b.totalDuration % 60).padStart(2, '0')}`
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {occurrences.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        {intl.formatMessage({
                          id: 'form.behaviorTracking.occurrenceLog',
                          defaultMessage: 'Occurrence log'
                        })}
                      </Typography>
                      <Stack spacing={0.5}>
                        {occurrences.slice(0, 50).map((o, i) => (
                          <Stack key={i} direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>
                              {o.time}
                            </Typography>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {o.behaviorName}
                            </Typography>
                            {o.setting && <Chip size="small" variant="outlined" label={o.setting} />}
                            <Chip size="small" variant="outlined" label={o.type === 'duration' ? `${o.duration ?? 0}s` : 'tally'} />
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              );
            }

            // ---------- Daily Mand ----------
            if (formType === 'daily-mand') {
              return <DailyMandDetail raw={raw} />;
            }

            // ---------- OT Data Sheet ----------
            if (formType === 'ot-data-sheet') {
              const goals = raw.goals || [];
              const PERF_COLOR = { i: 'success', minA: 'info', modA: 'warning', maxA: 'error', d: 'default' };
              return (
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={intl.formatMessage(
                        { id: 'calendar.dataSummary.goalsTracked', defaultMessage: '{count} goals tracked' },
                        { count: goals.length }
                      )}
                    />
                  </Stack>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={40}>#</TableCell>
                          <TableCell>{intl.formatMessage({ id: 'form.otDataSheet.col.area', defaultMessage: 'Area' })}</TableCell>
                          <TableCell>{intl.formatMessage({ id: 'form.otDataSheet.col.goal', defaultMessage: 'Goal' })}</TableCell>
                          <TableCell>
                            {intl.formatMessage({ id: 'form.otDataSheet.col.activities', defaultMessage: 'Activities' })}
                          </TableCell>
                          <TableCell align="center" width={130}>
                            {intl.formatMessage({ id: 'form.otDataSheet.col.performance', defaultMessage: 'Performance' })}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {goals.map((g, i) => (
                          <TableRow key={g.id || i}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>
                              {intl.formatMessage({ id: `form.otDataSheet.area.${g.area}`, defaultMessage: g.area || '—' })}
                            </TableCell>
                            <TableCell>{g.goal || '—'}</TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                {formatActivityList(g.activities)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              {g.performance ? (
                                <Chip
                                  size="small"
                                  color={PERF_COLOR[g.performance] || 'default'}
                                  label={intl.formatMessage({
                                    id: `form.otDataSheet.perf.${g.performance}`,
                                    defaultMessage: g.performance
                                  })}
                                />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {raw.notes && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {intl.formatMessage({ id: 'form.label.notes' })}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {raw.notes}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              );
            }

            // ---------- SLP Data Sheet ----------
            if (formType === 'slp-data-sheet') {
              const goals = raw.goals || [];
              const pct = (c, t) => (Number(t) > 0 ? Math.round((Number(c) / Number(t)) * 100) : null);
              const pctColor = (c, t) => {
                const p = pct(c, t);
                if (p == null) return 'default';
                return p >= 80 ? 'success' : p >= 50 ? 'warning' : 'error';
              };
              return (
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      size="small"
                      label={intl.formatMessage(
                        { id: 'calendar.dataSummary.goalsTracked', defaultMessage: '{count} goals tracked' },
                        { count: goals.length }
                      )}
                    />
                  </Stack>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={40}>#</TableCell>
                          <TableCell>{intl.formatMessage({ id: 'form.slpDataSheet.col.area', defaultMessage: 'Area' })}</TableCell>
                          <TableCell>{intl.formatMessage({ id: 'form.slpDataSheet.col.goal', defaultMessage: 'Goal' })}</TableCell>
                          <TableCell>
                            {intl.formatMessage({ id: 'form.slpDataSheet.col.activities', defaultMessage: 'Activities' })}
                          </TableCell>
                          <TableCell align="center" width={90}>
                            {intl.formatMessage({ id: 'form.slpDataSheet.col.score', defaultMessage: 'Score' })}
                          </TableCell>
                          <TableCell align="center" width={90}>
                            {intl.formatMessage({ id: 'form.slpDataSheet.col.accuracy', defaultMessage: 'Accuracy' })}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {goals.map((g, i) => {
                          const p = pct(g.correct, g.total);
                          return (
                            <TableRow key={g.id || i}>
                              <TableCell>{i + 1}</TableCell>
                              <TableCell>
                                {intl.formatMessage({ id: `form.slpDataSheet.area.${g.area}`, defaultMessage: g.area || '—' })}
                              </TableCell>
                              <TableCell>{g.goal || '—'}</TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                  {formatActivityList(g.activities)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">{Number(g.total) > 0 ? `${g.correct || 0}/${g.total}` : '—'}</TableCell>
                              <TableCell align="center">
                                {p != null ? <Chip size="small" color={pctColor(g.correct, g.total)} label={`${p}%`} /> : '—'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {raw.notes && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {intl.formatMessage({ id: 'form.label.notes' })}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {raw.notes}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              );
            }

            // ---------- ABC ----------
            if (formType === 'abc') {
              const records = raw.records || [];
              return (
                <Stack spacing={2.5}>
                  <Chip size="small" label={`${records.length} record(s)`} sx={{ alignSelf: 'flex-start' }} />
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={80}>Time</TableCell>
                          <TableCell>Setting</TableCell>
                          <TableCell>Antecedent</TableCell>
                          <TableCell>Behavior</TableCell>
                          <TableCell>Consequence</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {records.map((r, i) => (
                          <TableRow key={i}>
                            <TableCell>{r.time}</TableCell>
                            <TableCell>{r.setting || '—'}</TableCell>
                            <TableCell>{r.antecedent || '—'}</TableCell>
                            <TableCell>{r.behavior || '—'}</TableCell>
                            <TableCell>{r.consequence || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              );
            }

            // ---------- Injury Report (rich detail with body diagram) ----------
            if (formType === 'injury-report') {
              const isClient = (raw.reportType || raw.type) === 'client';
              const yesNo = (v) => (v === 'yes' ? intl.formatMessage({ id: 'common.yes' }) : intl.formatMessage({ id: 'common.no' }));
              const field = (label, value) => (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {value || '—'}
                  </Typography>
                </Grid>
              );
              const longField = (label, value) =>
                value ? (
                  <Grid size={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      {label}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {value}
                    </Typography>
                  </Grid>
                ) : null;
              const sectionTitle = (label) => (
                <Grid size={12}>
                  <Typography variant="subtitle2" color="primary.main" fontWeight={700} sx={{ mt: 1 }}>
                    {label}
                  </Typography>
                  <Divider sx={{ mt: 0.5 }} />
                </Grid>
              );
              const formatDateSafe = (v) => {
                if (!v) return '';
                try {
                  return new Date(v).toLocaleString(dateLocaleTag, { month: 'short', day: 'numeric', year: 'numeric' });
                } catch {
                  return String(v);
                }
              };
              const formatTimeSafe = (v) => {
                if (!v) return '';
                try {
                  return new Date(v).toLocaleTimeString(dateLocaleTag, { hour: '2-digit', minute: '2-digit' });
                } catch {
                  return String(v);
                }
              };
              const injuredName = raw.injuredLearnerName || raw.injuredStaffName || raw.injuredName || '';
              const locations = Array.isArray(raw.injuryLocations) ? raw.injuryLocations : [];

              return (
                <Stack spacing={2.5}>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Chip label={isClient ? 'Child' : 'Staff'} color={isClient ? 'primary' : 'secondary'} variant="outlined" size="small" />
                    <Chip label={`${formatDateSafe(raw.dateOfInjury)} · ${formatTimeSafe(raw.timeOfInjury)}`} size="small" />
                  </Stack>

                  <Grid container spacing={2}>
                    {sectionTitle(intl.formatMessage({ id: 'form.injuryReport.basicInfo', defaultMessage: 'Basic Information' }))}
                    {field(intl.formatMessage({ id: 'form.injuryReport.injuredPerson' }), injuredName)}
                    {field(
                      isClient
                        ? intl.formatMessage({ id: 'form.injuryReport.staffPresent', defaultMessage: 'Staff Present' })
                        : intl.formatMessage({ id: 'form.injuryReport.witnesses', defaultMessage: 'Witnesses Present' }),
                      isClient ? raw.staffPresent : raw.witnessesPresent
                    )}

                    <Grid size={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        {intl.formatMessage({ id: 'form.injuryReport.injuryLocation' })}
                      </Typography>
                      <Paper variant="outlined" sx={{ mt: 1, p: 1.5 }}>
                        <BodyDiagram selectedParts={locations} disabled />
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                          {locations.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          ) : (
                            locations.map((loc, idx) => <Chip key={`${loc}-${idx}`} label={loc} size="small" variant="outlined" />)
                          )}
                        </Stack>
                      </Paper>
                    </Grid>

                    {longField(
                      intl.formatMessage({ id: 'form.injuryReport.incidentDescription', defaultMessage: 'Incident Description' }),
                      raw.incidentDescription
                    )}

                    {(raw.antecedent || raw.behavior || raw.consequence || raw.duration) &&
                      sectionTitle(
                        intl.formatMessage({
                          id: 'form.injuryReport.abcSection',
                          defaultMessage: 'Antecedent / Behavior / Consequence'
                        })
                      )}
                    {longField(intl.formatMessage({ id: 'form.injuryReport.antecedent', defaultMessage: 'Antecedent' }), raw.antecedent)}
                    {longField(intl.formatMessage({ id: 'form.injuryReport.behavior' }), raw.behavior)}
                    {longField(intl.formatMessage({ id: 'form.injuryReport.consequence', defaultMessage: 'Consequence' }), raw.consequence)}
                    {raw.duration &&
                      field(intl.formatMessage({ id: 'form.injuryReport.duration', defaultMessage: 'Duration' }), `${raw.duration} min`)}

                    {sectionTitle(intl.formatMessage({ id: 'form.injuryReport.medicalSection', defaultMessage: 'First Aid & Medical' }))}
                    {field(intl.formatMessage({ id: 'form.injuryReport.firstAid' }), yesNo(raw.firstAidAdministered))}
                    {raw.firstAidByWhom &&
                      field(
                        intl.formatMessage({ id: 'form.injuryReport.firstAidByWhom', defaultMessage: 'First Aid By' }),
                        raw.firstAidByWhom
                      )}
                    {field(
                      intl.formatMessage({ id: 'form.injuryReport.hospitalVisit', defaultMessage: 'Hospital Visit Needed' }),
                      yesNo(raw.hospitalVisitNeeded)
                    )}
                    {longField(
                      intl.formatMessage({ id: 'form.injuryReport.hospitalDetails', defaultMessage: 'Hospital Details' }),
                      raw.hospitalDetails
                    )}

                    {(raw.parentNotified || raw.administratorNotified || raw.emergencyContactNotified) &&
                      sectionTitle(intl.formatMessage({ id: 'form.injuryReport.notificationsSection', defaultMessage: 'Notifications' }))}
                    {isClient &&
                      raw.parentNotified &&
                      field(
                        intl.formatMessage({ id: 'form.injuryReport.parentNotified', defaultMessage: 'Parent Notified' }),
                        yesNo(raw.parentNotified)
                      )}
                    {longField(
                      intl.formatMessage({
                        id: 'form.injuryReport.parentNotifiedDetails',
                        defaultMessage: 'Parent Notification Details'
                      }),
                      raw.parentNotifiedDetails
                    )}
                    {!isClient &&
                      raw.administratorNotified &&
                      field(
                        intl.formatMessage({ id: 'form.injuryReport.adminNotified', defaultMessage: 'Administrator Notified' }),
                        yesNo(raw.administratorNotified)
                      )}
                    {!isClient &&
                      raw.emergencyContactNotified &&
                      field(
                        intl.formatMessage({
                          id: 'form.injuryReport.emergencyContactNotified',
                          defaultMessage: 'Emergency Contact Notified'
                        }),
                        yesNo(raw.emergencyContactNotified)
                      )}
                    {longField(
                      intl.formatMessage({
                        id: 'form.injuryReport.emergencyContactDetails',
                        defaultMessage: 'Emergency Contact Details'
                      }),
                      raw.emergencyContactNotifiedDetails
                    )}

                    {raw.environmentDamage === 'yes' &&
                      sectionTitle(
                        intl.formatMessage({
                          id: 'form.injuryReport.environmentSection',
                          defaultMessage: 'Environment / Property Damage'
                        })
                      )}
                    {raw.environmentDamage === 'yes' &&
                      longField(
                        intl.formatMessage({
                          id: 'form.injuryReport.environmentDamageDescription',
                          defaultMessage: 'Description'
                        }),
                        raw.environmentDamageDescription
                      )}
                    {raw.environmentDamage === 'yes' &&
                      longField(
                        intl.formatMessage({ id: 'form.injuryReport.propertyDamaged', defaultMessage: 'Property Damaged' }),
                        raw.propertyDamaged
                      )}

                    {Array.isArray(raw.injuryPhotos) && raw.injuryPhotos.length > 0 && (
                      <>
                        {sectionTitle(intl.formatMessage({ id: 'form.injuryReport.photosSection', defaultMessage: 'Injury Photos' }))}
                        <Grid size={12}>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {raw.injuryPhotos.map((photo, idx) => (
                              <ServerImage
                                key={photo?.id || idx}
                                photo={photo}
                                alt={photo?.name || `Injury photo ${idx + 1}`}
                                sx={{
                                  width: 120,
                                  height: 120,
                                  objectFit: 'cover',
                                  borderRadius: 1,
                                  border: 1,
                                  borderColor: 'divider'
                                }}
                              />
                            ))}
                          </Stack>
                        </Grid>
                      </>
                    )}

                    {(raw.adminSignature || raw.parentSignature || raw.therapistSignature || raw.injuredTherapistSignature) && (
                      <>
                        {sectionTitle(intl.formatMessage({ id: 'form.injuryReport.signaturesSection', defaultMessage: 'Signatures' }))}
                        {raw.adminSignature && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({ id: 'form.injuryReport.adminSignature', defaultMessage: 'Administrator' })}
                            </Typography>
                            <Box
                              component="img"
                              src={raw.adminSignature}
                              alt="Administrator signature"
                              sx={{
                                display: 'block',
                                width: '100%',
                                maxWidth: 280,
                                height: 90,
                                objectFit: 'contain',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                              }}
                            />
                          </Grid>
                        )}
                        {raw.parentSignature && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({ id: 'form.injuryReport.parentSignature', defaultMessage: 'Parent / Guardian' })}
                            </Typography>
                            <Box
                              component="img"
                              src={raw.parentSignature}
                              alt="Parent signature"
                              sx={{
                                display: 'block',
                                width: '100%',
                                maxWidth: 280,
                                height: 90,
                                objectFit: 'contain',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                              }}
                            />
                          </Grid>
                        )}
                        {raw.therapistSignature && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({ id: 'form.injuryReport.therapistSignature', defaultMessage: 'Therapist' })}
                            </Typography>
                            <Box
                              component="img"
                              src={raw.therapistSignature}
                              alt="Therapist signature"
                              sx={{
                                display: 'block',
                                width: '100%',
                                maxWidth: 280,
                                height: 90,
                                objectFit: 'contain',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                              }}
                            />
                          </Grid>
                        )}
                        {raw.injuredTherapistSignature && (
                          <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="caption" color="text.secondary">
                              {intl.formatMessage({
                                id: 'form.injuryReport.injuredTherapistSignature',
                                defaultMessage: 'Injured Therapist'
                              })}
                            </Typography>
                            <Box
                              component="img"
                              src={raw.injuredTherapistSignature}
                              alt="Injured therapist signature"
                              sx={{
                                display: 'block',
                                width: '100%',
                                maxWidth: 280,
                                height: 90,
                                objectFit: 'contain',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                bgcolor: 'background.paper'
                              }}
                            />
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </Stack>
              );
            }
            // ---------- Fallback ----------
            return (
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({
                  id: 'calendar.viewData.noDetailRenderer',
                  defaultMessage: 'Detailed view is not available for this data sheet yet.'
                })}
              </Typography>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailEntry(null)}>{intl.formatMessage({ id: 'common.close' })}</Button>
        </DialogActions>
      </Dialog>

      {/* Date mismatch warning dialog */}
      <Dialog open={dateMismatchOpen} onClose={() => setDateMismatchOpen(false)} maxWidth="xs" slots={{ transition: PopupTransition }}>
        <DialogTitle sx={{ color: 'warning.main' }}>{intl.formatMessage({ id: 'calendar.dateMismatchDialog.title' })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage(
              { id: 'calendar.dateMismatchDialog.message' },
              {
                date: selectedEvent ? formatDate(selectedEvent.sessionDate || selectedEvent.start || selectedEvent.date) : ''
              }
            )}
            <br />
            <br />
            {intl.formatMessage({ id: 'calendar.dateMismatchDialog.confirmation' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setDateMismatchOpen(false)} color="secondary">
            {intl.formatMessage({ id: 'calendar.dateMismatchDialog.cancel' })}
          </Button>
          <Button onClick={doStartSession} variant="contained" color="warning">
            {intl.formatMessage({ id: 'calendar.dateMismatchDialog.startAnyway' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TherapistCalendar;
